import React, { useEffect, useState } from "react";
import api from "../../api";
import { Loader2, Plus, Trash2, Pencil, X, CalendarDays, MapPin, Tag, Copy, AlertTriangle, ImageOff, Repeat, Flame, GripVertical, RotateCcw, Download, CheckSquare, Ban, Archive, CalendarClock, CheckCircle2, UserCog, Send, Code2, PoundSterling, Lock, Link2, Key, QrCode, Mail, Utensils, FileText } from "lucide-react";
import { toast } from "../../hooks/use-toast";
import { useAuth } from "../../context/AuthContext";
import ImageUpload from "../../components/ImageUpload";
import { downloadCsv, downloadPdf } from "../../lib/download";
import { SeatMap, tiersToSections, availToSections } from "../../components/SeatMap";

const SECTION_PALETTE = ["#2ec9c9", "#f59e0b", "#8b5cf6", "#ef4444", "#10b981", "#3b82f6", "#ec4899", "#14b8a6"];

const empty = {
  title: "", description: "", long_description: "", terms: "", image_url: "", gallery_images: [],
  active: true, event_date: "", event_end: "", sold_out: false, venue_name: "", venue_address: "",
  ticket_tiers: [{ name: "General Admission", price: "", capacity: "" }],
  seo_title: "", seo_description: "", share_image: "", category: "", city: "", allow_venue_access: false, act_ids: [], lineup_artist_ids: [], lineup_set_times: {}, private: false, access_password: "", tickets_disabled: false, is_dinner: false, food_menu_url: "",
};

const CATEGORIES = ["Music", "Comedy", "Food & Drink", "Nightlife", "Arts & Theatre", "Sports", "Community", "Business", "Family", "Other"];

const fmtDate = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }); }
  catch { return String(iso).slice(0, 16).replace("T", " "); }
};

export default function ProductsTab() {
  const { user } = useAuth();
  const storeSlug = user?.business?.slug || "";
  const [embed, setEmbed] = useState(null); // event to show embed snippet for
  const [invite, setInvite] = useState(null); // { event, emails, busy } for private guest invites
  const [payouts, setPayouts] = useState(null); // { event, data } artist payouts for an event
  const openPayouts = async (p) => {
    setPayouts({ event: p, data: null });
    try { const { data } = await api.get(`/business/events/${p.id}/artist-payouts`); setPayouts({ event: p, data }); }
    catch { setPayouts({ event: p, data: { artists: [], tickets_sold: 0, gross: 0, total_artist_pay: 0 } }); }
  };
  const [events, setEvents] = useState(null);
  const [attendance, setAttendance] = useState({});
  const [daySel, setDaySel] = useState(() => new Date().toISOString().slice(0, 10));
  const [dayStats, setDayStats] = useState(null);
  const [discounts, setDiscounts] = useState([]);
  const [modal, setModal] = useState(null);
  const [heatmap, setHeatmap] = useState(null); // { event, stats }
  const [staffModal, setStaffModal] = useState(null); // { event, staff:[], selected:Set, busy }
  const [promo, setPromo] = useState(null); // { source, target_event_id, subject, message, include_details, discount_code, checked_in_only, audience, busy }
  const [promoResults, setPromoResults] = useState(null); // null=closed, []=loading→list
  const openPromoResults = () => { setPromoResults([]); api.get("/business/promos").then(({ data }) => setPromoResults(data.promos || [])).catch(() => setPromoResults([])); };
  const openPromo = (p) => setPromo({ source: p, target_event_id: "", subject: "", message: "", include_details: true, discount_code: "", checked_in_only: false, source_event_ids: [], exclude_target_buyers: false, audience: null, busy: false });
  const sendPromo = async (preview = false) => {
    if (!preview && !promo.target_event_id) { toast({ title: "Pick a show to promote", variant: "destructive" }); return; }
    if (!promo.message.trim()) { toast({ title: "Write a short message", variant: "destructive" }); return; }
    if (preview && !promo.target_event_id) { toast({ title: "Pick a show to promote first", variant: "destructive" }); return; }
    setPromo({ ...promo, busy: true });
    try {
      const { data } = await api.post(`/business/events/${promo.source.id}/promote`, {
        target_event_id: promo.target_event_id, subject: promo.subject, message: promo.message,
        include_details: promo.include_details, discount_code: promo.discount_code, checked_in_only: promo.checked_in_only,
        source_event_ids: promo.source_event_ids, exclude_target_buyers: promo.exclude_target_buyers, preview,
      });
      if (preview) { toast({ title: "Test sent to you ✉️", description: `Check ${data.to}.` }); setPromo({ ...promo, busy: false }); }
      else { toast({ title: "Promo sent 🎉", description: `Emailed ${data.sent} ticket-holder${data.sent !== 1 ? "s" : ""}${data.failed ? ` (${data.failed} failed)` : ""}.` }); setPromo(null); }
    } catch (e) {
      toast({ title: "Couldn't send", description: e?.response?.data?.detail || "Try again.", variant: "destructive" });
      setPromo({ ...promo, busy: false });
    }
  };
  const openStaffModal = async (p) => {
    setStaffModal({ event: p, staff: null, selected: new Set(), busy: false });
    try {
      const { data } = await api.get(`/business/products/${p.id}/staff`);
      setStaffModal({ event: p, staff: data.staff || [], selected: new Set((data.staff || []).filter((s) => s.assigned).map((s) => s.id)), busy: false });
    } catch (e) {
      toast({ title: "Couldn't load staff", description: e?.response?.data?.detail || "Try again", variant: "destructive" });
      setStaffModal(null);
    }
  };
  const saveEventStaff = async () => {
    if (!staffModal) return;
    setStaffModal({ ...staffModal, busy: true });
    try {
      await api.post(`/business/products/${staffModal.event.id}/staff`, { staff_ids: [...staffModal.selected] });
      toast({ title: "Door staff updated", description: `${staffModal.selected.size} assigned to ${staffModal.event.title}.` });
      setStaffModal(null);
    } catch (e) {
      toast({ title: "Couldn't save", description: e?.response?.data?.detail || "Try again", variant: "destructive" });
      setStaffModal({ ...staffModal, busy: false });
    }
  };
  const openHeatmap = async (p) => {
    setHeatmap({ event: p, stats: null });
    try {
      const { data } = await api.get(`/business/products/${p.id}/seatmap-stats`);
      setHeatmap({ event: p, stats: data });
    } catch (e) {
      toast({ title: "Could not load seat sales", description: e?.response?.data?.detail || "Try again", variant: "destructive" });
      setHeatmap(null);
    }
  };
  const [categories, setCategories] = useState(CATEGORIES);
  const [cities, setCities] = useState([]);
  const [venues, setVenues] = useState([]);
  const [acts, setActs] = useState([]);
  const [rosterArtists, setRosterArtists] = useState([]);
  const [newAct, setNewAct] = useState("");
  const [newVenue, setNewVenue] = useState("");
  const [broken, setBroken] = useState({});
  const [evQuery, setEvQuery] = useState("");
  const [evStatus, setEvStatus] = useState("all");
  const [evTab, setEvTab] = useState("live");
  const [evCity, setEvCity] = useState("all");
  const [restoreModal, setRestoreModal] = useState(null);
  const [restoreDate, setRestoreDate] = useState("");
  const [cancelModal, setCancelModal] = useState(null);
  const [cancelMsg, setCancelMsg] = useState("");
  const [cancelRefund, setCancelRefund] = useState(false);
  const [cancelNotify, setCancelNotify] = useState(true);
  const [cancelBusy, setCancelBusy] = useState(false);
  const [cancelResult, setCancelResult] = useState(null);
  const [postponeModal, setPostponeModal] = useState(null);
  const [postponeMsg, setPostponeMsg] = useState("");
  const [postponeDate, setPostponeDate] = useState("");
  const [postponeNotify, setPostponeNotify] = useState(true);
  const [postponeBusy, setPostponeBusy] = useState(false);

  const load = () => {
    api.get("/business/products").then(({ data }) => setEvents(data));
    api.get("/business/attendance").then(({ data }) => setAttendance(data)).catch(() => {});
  };
  useEffect(() => {
    load();
    api.get("/categories").then(({ data }) => { if (Array.isArray(data) && data.length) setCategories(data); }).catch(() => {});
    api.get("/cities").then(({ data }) => { if (Array.isArray(data)) setCities(data.map((c) => (typeof c === "string" ? c : c.name)).filter(Boolean)); }).catch(() => {});
    api.get("/business/acts").then(({ data }) => setActs(data || [])).catch(() => {});
    api.get("/business/artists").then(({ data }) => setRosterArtists(data || [])).catch(() => {});
    api.get("/business/discounts").then(({ data }) => setDiscounts(data || [])).catch(() => {});
    const iv = setInterval(() => api.get("/business/attendance").then(({ data }) => setAttendance(data)).catch(() => {}), 20000);
    return () => clearInterval(iv);
  }, []);

  useEffect(() => {
    if (!daySel) { setDayStats(null); return; }
    api.get(`/business/day-stats?date=${daySel}`).then(({ data }) => setDayStats(data)).catch(() => setDayStats(null));
  }, [daySel, events]);

  useEffect(() => {
    if (!promo?.source?.id) return;
    const params = new URLSearchParams({
      checked_in_only: promo.checked_in_only ? "true" : "false",
      source_ids: (promo.source_event_ids || []).join(","),
      target_id: promo.target_event_id || "",
      exclude_target_buyers: promo.exclude_target_buyers ? "true" : "false",
    });
    api.get(`/business/events/${promo.source.id}/audience?${params}`)
      .then(({ data }) => setPromo((cur) => cur ? { ...cur, audience: data.count } : cur)).catch(() => {});
  }, [promo?.source?.id, promo?.checked_in_only, promo?.target_event_id, promo?.exclude_target_buyers, JSON.stringify(promo?.source_event_ids)]); // eslint-disable-line


  const toggleAct = (id) => setModal((m) => {
    const cur = m.act_ids || [];
    return { ...m, act_ids: cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id] };
  });
  const toggleLineup = (id) => setModal((m) => {
    const cur = m.lineup_artist_ids || [];
    return { ...m, lineup_artist_ids: cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id] };
  });
  const [dragIdx, setDragIdx] = useState(null);
  const [lineupDragIdx, setLineupDragIdx] = useState(null);
  const reorderActs = (from, to) => setModal((m) => {
    const ids = [...(m.act_ids || [])];
    if (from == null || to == null || from === to || from < 0 || to < 0 || from >= ids.length || to >= ids.length) return m;
    const [x] = ids.splice(from, 1); ids.splice(to, 0, x);
    return { ...m, act_ids: ids };
  });
  const reorderLineup = (from, to) => setModal((m) => {
    const ids = [...(m.lineup_artist_ids || [])];
    if (from == null || to == null || from === to || from < 0 || to < 0 || from >= ids.length || to >= ids.length) return m;
    const [x] = ids.splice(from, 1); ids.splice(to, 0, x);
    return { ...m, lineup_artist_ids: ids };
  });
  const addActInline = async () => {
    const name = newAct.trim();
    if (!name) return;
    try {
      const { data } = await api.post("/business/acts", { name });
      setActs((a) => [...a, data].sort((x, y) => x.name.localeCompare(y.name)));
      setModal((m) => ({ ...m, act_ids: [...(m.act_ids || []), data.id] }));
      setNewAct("");
      toast({ title: "Artist added", description: `${data.name} added to your database.` });
    } catch { toast({ title: "Error", description: "Could not add artist", variant: "destructive" }); }
  };

  useEffect(() => {
    if (modal?.city) {
      api.get(`/venues?city=${encodeURIComponent(modal.city)}`).then(({ data }) => setVenues(data || [])).catch(() => setVenues([]));
    } else {
      setVenues([]);
    }
  }, [modal?.city]);

  const addVenueInline = async () => {
    const name = newVenue.trim();
    if (!name || !modal?.city) return;
    try {
      const { data } = await api.post("/business/venues", { name, city: modal.city });
      setVenues((vs) => (vs.some((v) => v.id === data.id) ? vs : [...vs, data].sort((a, b) => a.name.localeCompare(b.name))));
      setModal((m) => ({ ...m, venue_name: data.name, venue_address: data.address || m.venue_address }));
      setNewVenue("");
      toast({ title: "Venue added", description: `${data.name} is now available for ${modal.city}.` });
    } catch {
      toast({ title: "Error", description: "Could not add venue", variant: "destructive" });
    }
  };

  const duplicate = (p) => {
    const copy = { ...p };
    delete copy.id; delete copy.slug; delete copy.sold; delete copy.checked_in;
    delete copy.fee_custom; delete copy.fee_custom_source; delete copy.fee_pct; delete copy.fee_fixed;
    delete copy.commission_pct_override; delete copy.commission_fixed_override; delete copy.homepage_pinned;
    setModal({
      ...copy,
      title: `${p.title} (copy)`,
      event_date: "",
      active: true,
      ticket_tiers: (p.ticket_tiers || []).map((t) => ({ name: t.name, price: String(t.price), free: !!t.free, capacity: t.capacity ? String(t.capacity) : "", seat_rows: t.seat_rows ? String(t.seat_rows) : "", seat_cols: t.seat_cols ? String(t.seat_cols) : "", rowSeatsStr: (t.row_seats || []).join(", "), aisleStr: (t.aisle_after || []).join(", "), section_color: t.section_color || "", table_mode: !!t.table_mode, tables: t.tables ? String(t.tables) : "", seats_per_table: t.seats_per_table ? String(t.seats_per_table) : "", table_discount_pct: t.table_discount_pct ? String(t.table_discount_pct) : "" })),
    });
    toast({ title: "Duplicated — set the new date", description: "Act details copied. Just pick the event date & time, then save." });
  };

  const repeatWeekly = async (p) => {
    if (!p.event_date) { toast({ title: "Set an event date first", description: "Weekly copies are dated from the original event.", variant: "destructive" }); return; }
    const n = window.prompt(`Create how many weekly copies of "${p.title}"?\nEach will be 1 week apart from ${new Date(p.event_date).toLocaleDateString("en-GB")}.`, "4");
    if (!n) return;
    const weeks = parseInt(n);
    if (!weeks || weeks < 1) return;
    let interval = (window.prompt("Interval? Type: weekly, fortnightly or monthly", "weekly") || "weekly").trim().toLowerCase();
    if (!["weekly", "fortnightly", "monthly"].includes(interval)) interval = "weekly";
    try {
      const { data } = await api.post(`/business/products/${p.id}/repeat-weekly`, { weeks, interval });
      toast({ title: `Created ${data.created} ${interval} copies`, description: "Tickets reset to zero on each." });
      load();
    } catch { toast({ title: "Error", description: "Could not repeat event", variant: "destructive" }); }
  };

  const parseCSVInts = (s) => String(s || "").split(",").map((x) => parseInt(x.trim())).filter((n) => !isNaN(n) && n > 0);

  const save = async (e) => {
    e.preventDefault();
    const tiers = (modal.ticket_tiers || []).filter((t) => t.name).map((t) => ({
      id: t.id || "", name: t.name, price: t.free ? 0 : (parseFloat(t.price) || 0), free: !!t.free, capacity: parseInt(t.capacity) || 0,
      seat_rows: parseInt(t.seat_rows) || 0, seat_cols: parseInt(t.seat_cols) || 0,
      row_seats: parseCSVInts(t.rowSeatsStr), aisle_after: parseCSVInts(t.aisleStr),
      section_color: t.section_color || "",
      table_mode: !!t.table_mode, tables: parseInt(t.tables) || 0, seats_per_table: parseInt(t.seats_per_table) || 0,
      table_discount_pct: parseFloat(t.table_discount_pct) || 0,
    }));
    if (tiers.length === 0) { toast({ title: "Add a ticket type", description: "Events need at least one ticket type.", variant: "destructive" }); return; }
    const { repeat_weeks: _rw, ...rest } = modal;
    const payload = { ...rest, ticket_tiers: tiers, price: 0, event_capacity: parseInt(modal.event_capacity) || 0 };
    const repeatWeeks = parseInt(modal.repeat_weeks) || 0;
    try {
      if (modal.id) {
        await api.put(`/business/products/${modal.id}`, payload);
      } else {
        const willRepeat = repeatWeeks > 0 && payload.event_date;
        const { data } = await api.post("/business/products", willRepeat ? { ...payload, defer_notify: true } : payload);
        if (willRepeat && data?.id) {
          const { data: rep } = await api.post(`/business/products/${data.id}/repeat-weekly`, { weeks: repeatWeeks, interval: modal.repeat_interval || "weekly", announce_source: true });
          toast({ title: `Event created + ${rep.created} copies`, description: "Recurring dates added." });
          setModal(null); load(); return;
        }
      }
      toast({ title: "Saved", description: "Event saved." });
      setModal(null); load();
    } catch (err) {
      toast({ title: "Error", description: err?.response?.data?.detail || "Failed to save", variant: "destructive" });
    }
  };

  const del = async (id) => { await api.delete(`/business/products/${id}`); toast({ title: "Deleted" }); load(); };
  const copyToClipboard = (text, okTitle, okDesc) =>
    navigator.clipboard.writeText(text)
      .then(() => toast({ title: okTitle, description: okDesc }))
      .catch(() => toast({ title: "Copy failed", description: "Copy it manually.", variant: "destructive" }));
  const privateLink = (p) => `${window.location.origin}/store/${storeSlug}/events/${p.slug}`;
  const [menuUploading, setMenuUploading] = useState(false);
  const uploadFoodMenu = async (file) => {
    if (!file) return;
    if (file.type !== "application/pdf") { toast({ title: "PDF only", description: "Please upload a PDF food menu.", variant: "destructive" }); return; }
    setMenuUploading(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setModal((m) => ({ ...m, food_menu_url: data.url }));
      toast({ title: "Food menu uploaded 🍽️" });
    } catch (e) {
      toast({ title: "Upload failed", description: e?.response?.data?.detail || "Try a PDF under 15MB.", variant: "destructive" });
    } finally { setMenuUploading(false); }
  };
  const downloadPoster = (p) =>
    downloadPdf(`/business/products/${p.id}/private-poster.pdf?origin=${encodeURIComponent(window.location.origin)}`, `${p.slug || "event"}-private-poster.pdf`)
      .catch(() => toast({ title: "Couldn't build poster", variant: "destructive" }));
  const sendInvites = async () => {
    const emails = (invite.emails || "").split(/[\s,;]+/).map((e) => e.trim()).filter(Boolean);
    if (emails.length === 0) { toast({ title: "Add at least one email", variant: "destructive" }); return; }
    setInvite({ ...invite, busy: true });
    try {
      const { data } = await api.post(`/business/products/${invite.event.id}/invite`, { emails, origin: window.location.origin });
      toast({ title: `Invited ${data.sent} guest${data.sent !== 1 ? "s" : ""} 🎟️`, description: `${data.failed?.length ? `${data.failed.length} failed. ` : ""}${data.invalid?.length ? `${data.invalid.length} invalid skipped.` : ""}`.trim() || "Emails sent." });
      setInvite(null);
    } catch (e) {
      toast({ title: "Couldn't send invites", description: e?.response?.data?.detail || "Try again.", variant: "destructive" });
      setInvite({ ...invite, busy: false });
    }
  };
  const [selected, setSelected] = useState(new Set());
  const toggleSel = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const exportEventSales = (p) => downloadCsv(`/business/products/${p.id}/sales.csv`, `${p.slug || "event"}-sales.csv`).catch(() => toast({ title: "Export failed", variant: "destructive" }));
  const bulkPurge = async () => {
    const ids = [...selected];
    if (ids.length === 0) return;
    if (!window.confirm(`Permanently delete ${ids.length} event(s) and all their ticket sales? This cannot be undone.`)) return;
    try {
      const { data } = await api.post("/business/products/bulk-purge", { ids });
      toast({ title: `Deleted ${data.purged} event(s)`, description: `${data.tickets_removed} ticket record(s) removed${data.skipped_active ? `, ${data.skipped_active} active event(s) skipped` : ""}.` });
      setSelected(new Set());
      load();
    } catch { toast({ title: "Bulk delete failed", variant: "destructive" }); }
  };
  const purge = async (p) => {
    if (!window.confirm(`Permanently delete "${p.title}"? This removes the event AND all its ticket sales from your financials and top events. This cannot be undone.`)) return;
    try {
      const { data } = await api.delete(`/business/products/${p.id}?purge=true`);
      toast({ title: "Permanently deleted", description: `${data.tickets_removed || 0} ticket record(s) removed from financials.` });
      load();
    } catch (e) {
      toast({ title: "Couldn't delete", description: e?.response?.data?.detail || "Try again", variant: "destructive" });
    }
  };
  const doRestore = async (id, event_date) => {
    await api.post(`/business/products/${id}/restore`, event_date ? { event_date } : {});
    toast({ title: "Event restored", description: "It's back on sale." });
    setRestoreModal(null); load();
  };
  const restore = (p) => {
    const past = (p.event_date || "").slice(0, 10) < new Date().toISOString().slice(0, 10);
    if (past) { setRestoreDate(p.event_date || ""); setRestoreModal(p); }
    else doRestore(p.id);
  };
  const toggleCancel = async (p) => {
    if (p.cancelled) {
      await api.post(`/business/products/${p.id}/cancel`, { value: false });
      toast({ title: "Cancellation lifted", description: "The event is back to normal." });
      load();
    } else {
      setCancelModal(p);
      setCancelMsg(p.cancellation_message || "");
      setCancelRefund(false);
      setCancelNotify(true);
    }
  };
  const confirmCancel = async () => {
    const p = cancelModal;
    setCancelBusy(true);
    try {
      const { data } = await api.post(`/business/products/${p.id}/cancel`, {
        value: true, message: cancelMsg.trim(), auto_refund: cancelRefund, notify: cancelNotify,
      });
      const a = data._action || {};
      setCancelModal(null);
      load();
      if (cancelRefund) {
        setCancelResult({ title: p.title, ...a });
      } else {
        toast({ title: "Event cancelled", description: a.emails_sent ? `${a.emails_sent} ticket holder(s) emailed.` : "Visitors now see a cancellation notice." });
      }
    } catch (e) {
      toast({ title: "Couldn't cancel", description: e?.response?.data?.detail || "Try again.", variant: "destructive" });
    } finally { setCancelBusy(false); }
  };

  const togglePostpone = async (p) => {
    if (p.postponed) {
      await api.post(`/business/products/${p.id}/postpone`, { value: false, notify: false });
      toast({ title: "Postponement lifted", description: "The event is back to normal." });
      load();
    } else {
      setPostponeModal(p);
      setPostponeMsg(p.postpone_message || "");
      setPostponeDate("");
      setPostponeNotify(true);
    }
  };
  const confirmPostpone = async () => {
    const p = postponeModal;
    setPostponeBusy(true);
    try {
      const { data } = await api.post(`/business/products/${p.id}/postpone`, {
        value: true, message: postponeMsg.trim(), new_date: postponeDate || null, notify: postponeNotify,
      });
      const a = data._action || {};
      toast({ title: "Event postponed", description: a.emails_sent ? `${a.emails_sent} ticket holder(s) emailed. Tickets stay valid.` : "Tickets stay valid; visitors see a postponed notice." });
      setPostponeModal(null);
      load();
    } catch (e) {
      toast({ title: "Couldn't postpone", description: e?.response?.data?.detail || "Try again.", variant: "destructive" });
    } finally { setPostponeBusy(false); }
  };
  const toggleArchive = async (p) => {
    const next = !p.archived;
    if (next && !window.confirm(`Archive "${p.title}"? Public links will show a 404, but it stays editable here.`)) return;
    await api.post(`/business/products/${p.id}/archive`, { value: next });
    toast({ title: next ? "Event archived" : "Event unarchived", description: next ? "Public links now return 404." : "Public links work again." });
    load();
  };

  const setTier = (i, k, val) => setModal({ ...modal, ticket_tiers: modal.ticket_tiers.map((t, ti) => ti === i ? { ...t, [k]: val } : t) });
  const addTier = () => setModal({ ...modal, ticket_tiers: [...(modal.ticket_tiers || []), { name: "", price: "", free: false, capacity: "", seat_rows: "", seat_cols: "", rowSeatsStr: "", aisleStr: "", section_color: "", table_mode: false, tables: "", seats_per_table: "", table_discount_pct: "" }] });
  const rmTier = (i) => setModal({ ...modal, ticket_tiers: modal.ticket_tiers.filter((_, ti) => ti !== i) });

  if (!events) return <Loader2 className="animate-spin text-[#2ec9c9]" size={32} />;

  const brokenCount = events.filter((e) => broken[e.id]).length;
  const todayStr = new Date().toISOString().slice(0, 10);
  const evCities = [...new Set(events.map((e) => e.city).filter(Boolean))].sort();
  const isPast = (p) => (p.event_date || "").slice(0, 10) < todayStr;
  const isClosed = (p) => isPast(p) || p.cancelled || p.archived || !p.active;
  const liveCount = events.filter((p) => !isClosed(p)).length;
  const closedCount = events.filter((p) => isClosed(p)).length;
  const filtered = events.filter((p) => {
    const q = evQuery.trim().toLowerCase();
    if (q && !`${p.title} ${p.venue_name || ""}`.toLowerCase().includes(q)) return false;
    if (evCity !== "all" && (p.city || "") !== evCity) return false;
    const closed = isClosed(p);
    if (evTab === "live") {
      if (closed) return false;
      if (evStatus === "soldout" && !(p.sold_out || p.auto_sold_out)) return false;
      if (evStatus === "nearly" && !p.low_stock) return false;
    } else {
      if (!closed) return false;
      if (evStatus === "past" && !isPast(p)) return false;
      if (evStatus === "cancelled" && !p.cancelled) return false;
      if (evStatus === "hidden" && !p.archived) return false;
      if (evStatus === "inactive" && !(!p.active && !p.archived && !p.cancelled && !isPast(p))) return false;
    }
    return true;
  });
  filtered.sort((a, b) => {
    const da = a.event_date || "", dbb = b.event_date || "";
    if (!da && !dbb) return 0;
    if (!da) return 1;
    if (!dbb) return -1;
    return evTab === "closed" ? dbb.localeCompare(da) : da.localeCompare(dbb);
  });
  const season = Object.values(attendance).reduce((a, x) => ({
    sold: a.sold + (x.total || 0),
    checkedIn: a.checkedIn + (x.checked_in || 0),
    revenue: a.revenue + (x.channels ? x.channels.online.revenue + x.channels.box_office.revenue : 0),
  }), { sold: 0, checkedIn: 0, revenue: 0 });
  const seasonTurnout = season.sold > 0 ? Math.round((season.checkedIn / season.sold) * 100) : 0;

  return (
    <div>
      <button onClick={() => setModal({ ...empty })} data-testid="add-event-btn" className="gp-btn-teal rounded-full px-5 py-2.5 text-sm font-medium flex items-center gap-2 mb-6">
        <Plus size={18} /> Create event
      </button>

      {events.length > 0 && season.sold > 0 && (
        <div className="grid grid-cols-3 gap-3 mb-6" data-testid="season-totals">
          <div className="bg-white rounded-2xl p-4 shadow-[0_8px_24px_rgba(20,30,55,0.06)]">
            <p className="text-[11px] text-[#8a97a8] uppercase tracking-wide">Tickets sold</p>
            <p className="text-2xl font-bold text-[#16243b] mt-1" data-testid="season-sold">{season.sold.toLocaleString()}</p>
            <p className="text-[11px] text-[#8a97a8]">across all events</p>
          </div>
          <div className="bg-white rounded-2xl p-4 shadow-[0_8px_24px_rgba(20,30,55,0.06)]">
            <p className="text-[11px] text-[#8a97a8] uppercase tracking-wide">Checked in</p>
            <p className="text-2xl font-bold text-[#16a34a] mt-1" data-testid="season-checkedin">{season.checkedIn.toLocaleString()}<span className="text-sm font-medium text-[#8a97a8]"> · {seasonTurnout}%</span></p>
            <p className="text-[11px] text-[#8a97a8]">overall turnout</p>
          </div>
          <div className="bg-white rounded-2xl p-4 shadow-[0_8px_24px_rgba(20,30,55,0.06)]">
            <p className="text-[11px] text-[#8a97a8] uppercase tracking-wide">Revenue</p>
            <p className="text-2xl font-bold text-[#2ec9c9] mt-1" data-testid="season-revenue">£{season.revenue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
            <p className="text-[11px] text-[#8a97a8]">online + box office</p>
          </div>
        </div>
      )}

      {events.length > 0 && (
        <div className="bg-white rounded-2xl p-4 shadow-[0_8px_24px_rgba(20,30,55,0.06)] mb-6" data-testid="day-stats">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="flex items-center gap-2">
              <CalendarDays size={18} className="text-[#2ec9c9]" />
              <span className="text-sm font-medium text-[#16243b]">Sales on</span>
              <input type="date" value={daySel} onChange={(e) => setDaySel(e.target.value)} data-testid="day-stats-date"
                className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm outline-none focus:border-[#2ec9c9]" />
              <button onClick={openPromoResults} data-testid="promo-results-btn"
                className="ml-2 text-xs text-[#1fb0b0] font-medium hover:underline flex items-center gap-1"><Send size={12} /> Promo results</button>
            </div>
            <div className="flex flex-wrap gap-x-8 gap-y-2 sm:ml-auto">
              <div>
                <span className="text-xl font-bold text-[#16243b]" data-testid="day-purchased-count">{dayStats ? dayStats.purchased_count : "—"}</span>
                <span className="text-sm text-[#8a97a8]"> tickets sold this day</span>
                {dayStats && dayStats.purchased_revenue > 0 && <span className="text-sm text-[#8a97a8]"> · £{dayStats.purchased_revenue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>}
              </div>
              <div>
                <span className="text-xl font-bold text-[#2ec9c9]" data-testid="day-events-sold">{dayStats ? dayStats.events_sold : "—"}</span>
                <span className="text-sm text-[#8a97a8]"> sold for {dayStats ? dayStats.events.length : 0} event{dayStats && dayStats.events.length !== 1 ? "s" : ""} on this day</span>
              </div>
            </div>
          </div>
          {dayStats && dayStats.events.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-3 pt-3 border-t border-gray-100">
              {dayStats.events.map((e) => (
                <span key={e.id} className="text-xs bg-[#f0fbfb] text-[#1fb0b0] rounded-full px-2.5 py-1" data-testid={`day-event-${e.id}`}>{e.title} · {e.sold} sold</span>
              ))}
            </div>
          )}
        </div>
      )}


      {events.length > 0 && (
        <>
          <div className="inline-flex rounded-xl bg-gray-100 p-1 mb-4" data-testid="event-tabs">
            <button onClick={() => { setEvTab("live"); setEvStatus("all"); }} data-testid="event-tab-live"
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${evTab === "live" ? "bg-white text-[#16243b] shadow-sm" : "text-[#8a97a8] hover:text-[#5a6b80]"}`}>
              Live events <span className="ml-1 text-xs opacity-70">({liveCount})</span>
            </button>
            <button onClick={() => { setEvTab("closed"); setEvStatus("all"); }} data-testid="event-tab-closed"
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${evTab === "closed" ? "bg-white text-[#16243b] shadow-sm" : "text-[#8a97a8] hover:text-[#5a6b80]"}`}>
              Past &amp; closed <span className="ml-1 text-xs opacity-70">({closedCount})</span>
            </button>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 mb-6" data-testid="event-filters">
            <div className="relative flex-1 min-w-0">
              <input value={evQuery} onChange={(e) => setEvQuery(e.target.value)} data-testid="event-search"
                placeholder="Search events or venues…"
                className="w-full rounded-xl border border-gray-200 pl-4 pr-4 py-2.5 outline-none focus:border-[#2ec9c9] text-sm" />
            </div>
            <select value={evStatus} onChange={(e) => setEvStatus(e.target.value)} data-testid="event-status-filter"
              className="w-full sm:w-auto max-w-full rounded-xl border border-gray-200 px-4 py-2.5 outline-none focus:border-[#2ec9c9] text-sm bg-white">
              {evTab === "live" ? (
                <>
                  <option value="all">All live</option>
                  <option value="soldout">Sold out</option>
                  <option value="nearly">Nearly full</option>
                </>
              ) : (
                <>
                  <option value="all">All past &amp; closed</option>
                  <option value="past">Past (date passed)</option>
                  <option value="cancelled">Cancelled</option>
                  <option value="hidden">Archived (404)</option>
                  <option value="inactive">Hidden / inactive</option>
                </>
              )}
            </select>
            {evCities.length > 0 && (
              <select value={evCity} onChange={(e) => setEvCity(e.target.value)} data-testid="event-city-filter"
                className="w-full sm:w-auto max-w-full rounded-xl border border-gray-200 px-4 py-2.5 outline-none focus:border-[#2ec9c9] text-sm bg-white">
                <option value="all">All cities</option>
                {evCities.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            )}
          </div>
        </>
      )}

      {brokenCount > 0 && (
        <div className="mb-6 flex items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-4" data-testid="reupload-banner">
          <AlertTriangle size={20} className="text-amber-500 shrink-0 mt-0.5" />
          <div className="text-sm text-[#8a5a00]">
            <p className="font-semibold">{brokenCount} event image{brokenCount !== 1 ? "s aren't" : " isn't"} loading.</p>
            <p className="mt-0.5">Your artwork may have been lost in a recent update. Open the affected event{brokenCount !== 1 ? "s" : ""} below and re-upload the main image so it shows on your storefront.</p>
          </div>
        </div>
      )}

      {events.length === 0 ? (
        <div className="bg-white rounded-2xl p-12 text-center text-[#8a97a8] shadow-[0_8px_24px_rgba(20,30,55,0.06)]">
          No events yet. Create your first event to start selling tickets.
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-2xl p-12 text-center text-[#8a97a8] shadow-[0_8px_24px_rgba(20,30,55,0.06)]" data-testid="events-no-match">
          No events match your filters.
        </div>
      ) : (
        <>
        {selected.size > 0 && (
          <div className="sticky top-2 z-20 mb-4 bg-[#16243b] text-white rounded-xl px-4 py-3 flex items-center justify-between gap-3 shadow-lg" data-testid="bulk-action-bar">
            <span className="text-sm font-medium flex items-center gap-2"><CheckSquare size={16} /> {selected.size} event(s) selected</span>
            <div className="flex items-center gap-2">
              <button onClick={() => setSelected(new Set())} className="text-xs text-[#9fb0c8] hover:text-white">Clear</button>
              <button onClick={bulkPurge} data-testid="bulk-purge-btn" className="bg-red-500 hover:bg-red-600 rounded-full px-4 py-1.5 text-sm font-medium flex items-center gap-1.5"><Trash2 size={14} /> Delete selected</button>
            </div>
          </div>
        )}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {filtered.map((p) => {
            const from = Math.min(...(p.ticket_tiers || [{ price: p.price }]).map((t) => t.price));
            const multiPrice = new Set((p.ticket_tiers || [{ price: p.price }]).map((t) => t.price)).size > 1;
            const sold = (p.ticket_tiers || []).reduce((s, t) => s + (t.sold || 0), 0);
            const att = attendance[p.id] || { total: 0, checked_in: 0, tiers: {} };
            const turnout = att.total > 0 ? Math.round((att.checked_in / att.total) * 100) : 0;
            const revenue = att.channels ? (att.channels.online.revenue + att.channels.box_office.revenue) : 0;
            return (
              <div key={p.id} className="bg-white rounded-2xl overflow-hidden shadow-[0_8px_24px_rgba(20,30,55,0.06)]" data-testid={`event-${p.id}`}>
                <div className="h-36 bg-gray-100 relative">
                  {p.cancelled && (
                    <span className="absolute top-2 left-2 z-10 inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-1 rounded-full bg-[#c0392b] text-white" data-testid={`cancelled-badge-${p.id}`}>
                      <Ban size={11} /> Cancelled
                    </span>
                  )}
                  {!p.cancelled && p.archived && (
                    <span className="absolute top-2 left-2 z-10 inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-1 rounded-full bg-[#6b21a8] text-white" data-testid={`archived-404-badge-${p.id}`}>
                      <Archive size={11} /> Archived (404)
                    </span>
                  )}
                  {!p.cancelled && !p.archived && p.postponed && (
                    <span className="absolute top-2 left-2 z-10 inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-1 rounded-full bg-[#d97706] text-white" data-testid={`postponed-badge-${p.id}`}>
                      <CalendarClock size={11} /> Postponed
                    </span>
                  )}
                  {!p.cancelled && !p.archived && !p.active && (
                    <span className="absolute top-2 left-2 z-10 inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-1 rounded-full bg-[#16243b]/85 text-white" data-testid={`archived-badge-${p.id}`}>
                      {p.auto_archived ? "Auto-archived" : "Hidden"}
                    </span>
                  )}
                  {p.private && (
                    <span className="absolute bottom-2 left-2 z-10 inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-1 rounded-full bg-[#0f172a]/85 text-white" data-testid={`private-badge-${p.id}`} title="Hidden from the public — direct link only">
                      <Lock size={11} /> Private{p.access_password ? " · locked" : ""}
                    </span>
                  )}
                  {p.sold_out && (
                    <span className="absolute top-2 right-2 z-10 inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-1 rounded-full bg-[#c0392b] text-white" data-testid={`soldout-badge-${p.id}`}>
                      Sold out
                    </span>
                  )}
                  {!p.sold_out && p.auto_sold_out && (
                    <span className="absolute top-2 right-2 z-10 inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-1 rounded-full bg-[#e67e22] text-white" data-testid={`auto-soldout-badge-${p.id}`} title="Every ticket type has sold out">
                      Sold out (full)
                    </span>
                  )}
                  {!p.sold_out && !p.auto_sold_out && p.low_stock && (
                    <span className="absolute top-2 right-2 z-10 inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-1 rounded-full bg-[#c0392b] text-white" data-testid={`lowstock-badge-${p.id}`} title="Tickets running low">
                      Only {p.tickets_left} left
                    </span>
                  )}
                  {p.image_url ? (
                    broken[p.id] ? (
                      <div className="w-full h-full flex flex-col items-center justify-center text-amber-500 bg-amber-50" data-testid={`broken-image-${p.id}`}>
                        <ImageOff size={22} />
                        <span className="text-[11px] font-medium mt-1 text-[#8a5a00]">Image not loading — re-upload</span>
                      </div>
                    ) : (
                      <img src={p.image_url} alt={p.title} className="w-full h-full object-cover"
                        onError={() => setBroken((b) => (b[p.id] ? b : { ...b, [p.id]: true }))} />
                    )
                  ) : null}
                </div>
                <div className="p-4">
                  <div className="flex justify-between items-start">
                    <h3 className="font-semibold text-[#16243b]">{p.title}</h3>
                    <span className="font-bold text-[#2ec9c9] whitespace-nowrap">{p.tickets_disabled ? "No tickets" : (from <= 0 ? "Free" : `${multiPrice ? "from " : ""}£${from.toFixed(2)}`)}</span>
                  </div>
                  {p.event_date && <p className="text-xs text-[#8a97a8] mt-1 flex items-center gap-1"><CalendarDays size={12} /> {fmtDate(p.event_date)}</p>}
                  {p.venue_name && <p className="text-xs text-[#8a97a8] mt-0.5 flex items-center gap-1"><MapPin size={12} /> {p.venue_name}</p>}
                  {p.fee_custom && (
                    <span className="inline-flex items-center gap-1 mt-2 text-xs font-medium text-[#8a5a00] bg-amber-100 px-2 py-1 rounded-full" data-testid={`fee-badge-${p.id}`}
                      title={`${p.fee_custom_source === "event" ? "This event has a custom platform fee" : "Your account has a custom platform fee"}: ${p.fee_pct}%${p.fee_fixed ? ` + £${p.fee_fixed.toFixed(2)}/ticket` : ""}.`}>
                      <Tag size={11} /> Custom fee: {p.fee_pct}%{p.fee_fixed ? ` + £${p.fee_fixed.toFixed(2)}` : ""}{p.fee_custom_source === "event" ? " (this event)" : ""}
                    </span>
                  )}

                  {evTab === "closed" && (
                    <div className="mt-3 rounded-xl border border-[#2ec9c9]/30 bg-[#f0fbfb] p-3" data-testid={`final-numbers-${p.id}`}>
                      <p className="text-[11px] font-semibold text-[#1fb0b0] uppercase tracking-wide mb-1.5">Final numbers</p>
                      <div className="grid grid-cols-3 gap-2 text-center">
                        <div>
                          <p className="text-lg font-bold text-[#16243b]" data-testid={`final-sold-${p.id}`}>{att.total || 0}</p>
                          <p className="text-[10px] text-[#8a97a8] uppercase">Sold</p>
                        </div>
                        <div>
                          <p className="text-lg font-bold text-[#16a34a]" data-testid={`final-checkedin-${p.id}`}>{att.checked_in || 0}<span className="text-xs font-medium text-[#8a97a8]"> · {turnout}%</span></p>
                          <p className="text-[10px] text-[#8a97a8] uppercase">Checked in</p>
                        </div>
                        <div>
                          <p className="text-lg font-bold text-[#16243b]" data-testid={`final-revenue-${p.id}`}>£{revenue.toFixed(2)}</p>
                          <p className="text-[10px] text-[#8a97a8] uppercase">Revenue</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Attendance */}
                  <div className="mt-3 rounded-xl bg-[#f6f8fb] p-3" data-testid={`attendance-${p.id}`}>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-[#16243b] font-semibold">{sold} sold</span>
                      <span className="text-[#5a6b80]"><span className="font-semibold text-[#16a34a]">{att.checked_in}</span> checked in · <span className="font-semibold">{turnout}%</span></span>
                    </div>
                    <div className="mt-2 h-1.5 rounded-full bg-gray-200 overflow-hidden">
                      <div className="h-full bg-[#2ec9c9] transition-all" style={{ width: `${turnout}%` }} />
                    </div>
                    <div className="mt-2 space-y-1">
                      {(p.ticket_tiers || []).map((t) => {
                        const ta = (att.tiers || {})[t.id] || { total: 0, checked_in: 0 };
                        return (
                          <div key={t.id} className="flex items-center justify-between text-[11px] text-[#8a97a8]">
                            <span>{t.name}</span>
                            <span>{t.sold || 0}{t.capacity > 0 ? `/${t.capacity}` : ""} sold · {ta.checked_in} in</span>
                          </div>
                        );
                      })}
                    </div>
                    {att.channels && (att.channels.online.count + att.channels.box_office.count + att.channels.comp.count) > 0 && (
                      <div className="mt-2 pt-2 border-t border-gray-200 flex flex-wrap gap-x-3 gap-y-1 text-[11px]" data-testid={`channels-${p.id}`}>
                        <span className="text-[#5a6b80]"><span className="inline-block w-2 h-2 rounded-full bg-[#2ec9c9] mr-1 align-middle" />Online {att.channels.online.count} · £{att.channels.online.revenue.toFixed(2)}</span>
                        <span className="text-[#5a6b80]"><span className="inline-block w-2 h-2 rounded-full bg-[#5b2fd6] mr-1 align-middle" />Box office {att.channels.box_office.count} · £{att.channels.box_office.revenue.toFixed(2)}</span>
                        <span className="text-[#5a6b80]"><span className="inline-block w-2 h-2 rounded-full bg-[#f4a340] mr-1 align-middle" />Comp {att.channels.comp.count}</span>
                      </div>
                    )}
                  </div>

                  {/* Payout snapshot */}
                  {att.payout && att.payout.gross > 0 && (
                    <div className="mt-3 rounded-xl border border-[#2ec9c9]/25 bg-[#f0fbfb] p-3" data-testid={`payout-snapshot-${p.id}`}>
                      <div className="flex items-center justify-between mb-1.5">
                        <p className="text-[11px] font-semibold text-[#1fb0b0] uppercase tracking-wide flex items-center gap-1"><PoundSterling size={11} /> Your payout</p>
                        <span className="text-[10px] text-[#8a97a8]">Fee {p.fee_pct}%{p.fee_fixed ? ` + £${p.fee_fixed.toFixed(2)}` : ""}/ticket</span>
                      </div>
                      {att.payout.fees_passed && (
                        <p className="text-[10px] text-[#16a34a] font-medium mb-1.5 flex items-center gap-1" data-testid={`fees-passed-note-${p.id}`}>
                          <CheckCircle2 size={11} /> Booking fees paid by customers — you keep the full total
                        </p>
                      )}
                      <div className="grid grid-cols-3 gap-2 text-center">
                        <div>
                          <p className="text-sm font-bold text-[#16243b]" data-testid={`payout-gross-${p.id}`}>£{att.payout.gross.toFixed(2)}</p>
                          <p className="text-[10px] text-[#8a97a8] uppercase">Gross</p>
                        </div>
                        <div>
                          <p className="text-sm font-bold text-[#f47c7c]" data-testid={`payout-fee-${p.id}`}>−£{att.payout.fee.toFixed(2)}</p>
                          <p className="text-[10px] text-[#8a97a8] uppercase">Platform fee</p>
                        </div>
                        <div>
                          <p className="text-sm font-bold text-[#16a34a]" data-testid={`payout-net-${p.id}`}>£{att.payout.net.toFixed(2)}</p>
                          <p className="text-[10px] text-[#8a97a8] uppercase">Net to you</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {p.private && (
                    <div className="mt-3 rounded-xl border border-[#0f172a]/15 bg-[#f6f8fb] p-3" data-testid={`private-share-${p.id}`}>
                      <p className="text-[11px] font-semibold text-[#0f172a] uppercase tracking-wide mb-2 flex items-center gap-1"><Lock size={11} /> Private — share directly</p>
                      <div className="flex gap-2">
                        <button onClick={() => copyToClipboard(privateLink(p), "Private link copied", "Share it with your invited guests.")} data-testid={`copy-private-link-${p.id}`}
                          className="flex-1 rounded-lg py-2 text-xs font-medium flex items-center justify-center gap-1.5 bg-[#2ec9c9] text-white hover:bg-[#1fb0b0] transition-colors">
                          <Link2 size={13} /> Copy link
                        </button>
                        {p.access_password && (
                          <button onClick={() => copyToClipboard(p.access_password, "Password copied", "Give it to your guests to unlock the event.")} data-testid={`copy-private-password-${p.id}`}
                            className="flex-1 rounded-lg py-2 text-xs font-medium flex items-center justify-center gap-1.5 border border-[#2ec9c9] text-[#1fb0b0] hover:bg-[#eefafa] transition-colors">
                            <Key size={13} /> Copy password
                          </button>
                        )}
                      </div>
                      <div className="flex gap-2 mt-2">
                        <button onClick={() => downloadPoster(p)} data-testid={`private-poster-${p.id}`}
                          className="flex-1 rounded-lg py-2 text-xs font-medium flex items-center justify-center gap-1.5 border border-gray-200 text-[#5a6b80] hover:border-[#2ec9c9] hover:text-[#1fb0b0] transition-colors">
                          <QrCode size={13} /> QR poster
                        </button>
                        <button onClick={() => setInvite({ event: p, emails: "", busy: false })} data-testid={`invite-guests-${p.id}`}
                          className="flex-1 rounded-lg py-2 text-xs font-medium flex items-center justify-center gap-1.5 border border-gray-200 text-[#5a6b80] hover:border-[#2ec9c9] hover:text-[#1fb0b0] transition-colors">
                          <Mail size={13} /> Invite guests
                        </button>
                      </div>
                    </div>
                  )}

                  <div className="flex gap-2 mt-4">
                    {!p.active && (
                      <button onClick={() => restore(p)} data-testid={`restore-event-${p.id}`} className="flex-1 rounded-lg py-2 text-sm font-medium flex items-center justify-center gap-1 bg-[#2ec9c9] text-white hover:bg-[#1fb0b0] transition-colors"><RotateCcw size={14} /> Restore</button>
                    )}
                    <button onClick={() => setModal({ ...p, ticket_tiers: (p.ticket_tiers || []).map((t) => ({ ...t, price: String(t.price), free: !!t.free, capacity: t.capacity ? String(t.capacity) : "", seat_rows: t.seat_rows ? String(t.seat_rows) : "", seat_cols: t.seat_cols ? String(t.seat_cols) : "", rowSeatsStr: (t.row_seats || []).join(", "), aisleStr: (t.aisle_after || []).join(", "), section_color: t.section_color || "", table_mode: !!t.table_mode, tables: t.tables ? String(t.tables) : "", seats_per_table: t.seats_per_table ? String(t.seats_per_table) : "", table_discount_pct: t.table_discount_pct ? String(t.table_discount_pct) : "" })), event_date: (p.event_date || "").slice(0, 16), event_end: (p.event_end || "").slice(0, 16) })} data-testid={`edit-event-${p.id}`} className="flex-1 gp-btn-outline rounded-lg py-2 text-sm font-medium flex items-center justify-center gap-1"><Pencil size={14} /> Edit</button>
                    <button onClick={() => duplicate(p)} data-testid={`duplicate-event-${p.id}`} title="Duplicate this event" className="px-3 rounded-lg border border-gray-200 text-[#5a6b80] hover:border-[#2ec9c9] hover:text-[#1fb0b0] transition-colors"><Copy size={16} /></button>
                    <button onClick={() => repeatWeekly(p)} data-testid={`repeat-weekly-${p.id}`} title="Repeat weekly — create several dated copies" className="px-3 rounded-lg border border-gray-200 text-[#5a6b80] hover:border-[#2ec9c9] hover:text-[#1fb0b0] transition-colors"><Repeat size={16} /></button>
                    {p.reserved_seating && (
                      <button onClick={() => openHeatmap(p)} data-testid={`seat-heatmap-${p.id}`} title="Seat sales heatmap" className="px-3 rounded-lg border border-gray-200 text-[#5a6b80] hover:border-[#ef4444] hover:text-[#ef4444] transition-colors"><Flame size={16} /></button>
                    )}
                    {p.active ? (
                      <button onClick={() => del(p.id)} data-testid={`delete-event-${p.id}`} title="Delete event" className="px-3 rounded-lg border border-red-200 text-red-500 hover:bg-red-50 transition-colors"><Trash2 size={16} /></button>
                    ) : (
                      <>
                        <button onClick={() => exportEventSales(p)} data-testid={`export-event-${p.id}`} title="Export sales CSV" className="px-3 rounded-lg border border-gray-200 text-[#5a6b80] hover:bg-gray-50 transition-colors"><Download size={16} /></button>
                        <label className="px-2 rounded-lg border border-gray-200 flex items-center cursor-pointer" title="Select for bulk delete">
                          <input type="checkbox" checked={selected.has(p.id)} onChange={() => toggleSel(p.id)} data-testid={`select-event-${p.id}`} className="accent-[#ef4444]" />
                        </label>
                        <button onClick={() => purge(p)} data-testid={`purge-event-${p.id}`} title="Delete permanently (removes sales from financials)" className="px-3 rounded-lg border border-red-300 text-red-600 hover:bg-red-50 transition-colors flex items-center gap-1 text-sm font-medium"><Trash2 size={16} /> Delete</button>
                      </>
                    )}
                  </div>
                  <div className="flex gap-2 mt-2">
                    <button onClick={() => openStaffModal(p)} data-testid={`assign-staff-${p.id}`}
                      className="flex-1 rounded-lg py-2 text-xs font-medium flex items-center justify-center gap-1 border border-gray-200 text-[#5a6b80] hover:border-[#2ec9c9] hover:text-[#1fb0b0] transition-colors">
                      <UserCog size={13} /> Door staff
                    </button>
                    <button onClick={() => openPromo(p)} data-testid={`promote-event-${p.id}`}
                      className="flex-1 rounded-lg py-2 text-xs font-medium flex items-center justify-center gap-1 border border-gray-200 text-[#5a6b80] hover:border-[#2ec9c9] hover:text-[#1fb0b0] transition-colors">
                      <Send size={13} /> Promote
                    </button>
                    <button onClick={() => setEmbed(p)} data-testid={`embed-event-${p.id}`}
                      className="flex-1 rounded-lg py-2 text-xs font-medium flex items-center justify-center gap-1 border border-gray-200 text-[#5a6b80] hover:border-[#2ec9c9] hover:text-[#1fb0b0] transition-colors">
                      <Code2 size={13} /> Embed
                    </button>
                    <button onClick={() => openPayouts(p)} data-testid={`artist-payouts-${p.id}`}
                      className="flex-1 rounded-lg py-2 text-xs font-medium flex items-center justify-center gap-1 border border-gray-200 text-[#5a6b80] hover:border-[#2ec9c9] hover:text-[#1fb0b0] transition-colors">
                      <PoundSterling size={13} /> Artist pay
                    </button>
                  </div>
                  <div className="flex gap-2 mt-2">
                    <button onClick={() => toggleCancel(p)} data-testid={`cancel-event-${p.id}`}
                      className={`flex-1 rounded-lg py-2 text-xs font-medium flex items-center justify-center gap-1 border transition-colors ${p.cancelled ? "border-[#2ec9c9] text-[#1fb0b0] hover:bg-[#eefafa]" : "border-red-200 text-red-500 hover:bg-red-50"}`}>
                      <Ban size={13} /> {p.cancelled ? "Un-cancel" : "Mark cancelled"}
                    </button>
                    <button onClick={() => togglePostpone(p)} data-testid={`postpone-event-${p.id}`}
                      className={`flex-1 rounded-lg py-2 text-xs font-medium flex items-center justify-center gap-1 border transition-colors ${p.postponed ? "border-[#2ec9c9] text-[#1fb0b0] hover:bg-[#eefafa]" : "border-amber-200 text-amber-600 hover:bg-amber-50"}`}>
                      <CalendarClock size={13} /> {p.postponed ? "Un-postpone" : "Postpone"}
                    </button>
                    <button onClick={() => toggleArchive(p)} data-testid={`archive-event-${p.id}`}
                      className={`flex-1 rounded-lg py-2 text-xs font-medium flex items-center justify-center gap-1 border transition-colors ${p.archived ? "border-[#2ec9c9] text-[#1fb0b0] hover:bg-[#eefafa]" : "border-gray-200 text-[#5a6b80] hover:bg-gray-50"}`}>
                      <Archive size={13} /> {p.archived ? "Unarchive" : "Archive (404)"}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        </>
      )}

      {payouts && (
        <div className="fixed inset-0 z-[80] bg-black/50 flex items-center justify-center p-4" onClick={() => setPayouts(null)} data-testid="payouts-modal">
          <div className="bg-white rounded-2xl w-full max-w-[520px] max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center p-5 border-b border-gray-100">
              <h3 className="font-bold text-[#16243b] flex items-center gap-2"><PoundSterling size={18} className="text-[#2ec9c9]" /> Artist payouts</h3>
              <button onClick={() => setPayouts(null)} data-testid="payouts-close"><X className="text-[#5a6b80]" /></button>
            </div>
            <div className="p-5">
              <p className="text-sm text-[#5a6b80] mb-1">{payouts.event.title}</p>
              {!payouts.data ? <Loader2 className="animate-spin text-[#2ec9c9] my-6 mx-auto" size={26} /> : (
                <>
                  <div className="rounded-xl bg-[#f6f8fb] p-3 mb-4 flex items-center justify-between text-sm">
                    <span className="text-[#5a6b80]">{payouts.data.tickets_sold} sold · £{Number(payouts.data.gross).toFixed(2)} gross</span>
                    <span className="font-bold text-[#16243b]">Total to artists: £{Number(payouts.data.total_artist_pay).toFixed(2)}</span>
                  </div>
                  {payouts.data.artists.length > 0 && (
                    <button onClick={() => downloadPdf(`/business/events/${payouts.event.id}/artist-payouts.pdf`, `${payouts.event.slug || "event"}-artist-settlement.pdf`)}
                      data-testid="payouts-pdf" className="mb-3 gp-btn-outline rounded-lg px-4 py-2 text-sm font-medium flex items-center gap-1.5"><Download size={14} /> Download settlement PDF</button>
                  )}
                  {payouts.data.artists.length === 0 ? (
                    <p className="text-sm text-[#8a97a8] py-4 text-center" data-testid="payouts-empty">No roster artists have a pay deal yet. Set deals in the <strong>Roster &amp; bookings</strong> tab.</p>
                  ) : (
                    <div className="space-y-2" data-testid="payouts-list">
                      {payouts.data.artists.map((a) => (
                        <div key={a.artist_id} className="flex items-center justify-between rounded-xl border border-gray-100 px-3 py-2.5">
                          <div>
                            <p className="font-medium text-[#16243b] text-sm">{a.name}</p>
                            <p className="text-xs text-[#8a97a8]">
                              {a.deal_type === "percent" ? `${a.value}% of sales` : a.deal_type === "per_ticket" ? `£${a.value}/ticket` : `£${a.value} flat`}
                              {a.min_fee ? `, min £${a.min_fee}` : ""}{a.used_override ? " · this gig's rate" : " · default rate"}
                            </p>
                          </div>
                          <span className="font-bold text-[#16a34a]" data-testid={`payout-amount-${a.artist_id}`}>£{a.pay != null ? Number(a.pay).toFixed(2) : "—"}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {invite && (
        <div className="fixed inset-0 z-[80] bg-black/50 flex items-center justify-center p-4" onClick={() => !invite.busy && setInvite(null)} data-testid="invite-modal">
          <div className="bg-white rounded-2xl w-full max-w-[480px] max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center p-5 border-b border-gray-100">
              <h3 className="font-bold text-[#16243b] flex items-center gap-2"><Mail size={18} className="text-[#2ec9c9]" /> Invite guests</h3>
              <button onClick={() => !invite.busy && setInvite(null)} data-testid="invite-close"><X className="text-[#5a6b80]" /></button>
            </div>
            <div className="p-5 space-y-4">
              <p className="text-sm text-[#5a6b80]">Email the private link{invite.event.access_password ? " and password" : ""} for <span className="font-semibold text-[#16243b]">{invite.event.title}</span> straight to your guests. They'll get a branded invite with a one-tap "Get your tickets" button.</p>
              <div>
                <label className="block text-sm font-medium text-[#16243b] mb-1.5">Guest emails</label>
                <textarea value={invite.emails} onChange={(e) => setInvite({ ...invite, emails: e.target.value })} data-testid="invite-emails"
                  placeholder="alex@email.com, sam@email.com&#10;or one per line" rows={6}
                  className="w-full rounded-xl border border-gray-200 px-4 py-2.5 outline-none focus:border-[#2ec9c9] text-sm resize-none" />
                <p className="text-xs text-[#8a97a8] mt-1">Separate with commas, spaces or new lines.</p>
              </div>
              <button onClick={sendInvites} disabled={invite.busy} data-testid="invite-send"
                className="w-full gp-btn-teal rounded-xl py-3 font-medium disabled:opacity-60 inline-flex items-center justify-center gap-2">
                {invite.busy ? <Loader2 className="animate-spin" size={18} /> : <Send size={16} />} Send invites
              </button>
            </div>
          </div>
        </div>
      )}


      {embed && (() => {
        const embedUrl = `${window.location.origin}/embed/store/${storeSlug}/events/${embed.slug}`;
        const frameId = `ticketspro-${embed.slug}`;
        const snippet = `<iframe id="${frameId}" src="${embedUrl}" style="width:100%;max-width:400px;border:0;background:transparent;overflow:hidden" scrolling="no" allowtransparency="true" title="${(embed.title || "Event").replace(/"/g, "&quot;")}"></iframe>
<script>window.addEventListener("message",function(e){if(e.data&&e.data.type==="ticketspro-embed-height"&&e.data.path&&e.data.path.indexOf("/${embed.slug}")>-1){var f=document.getElementById("${frameId}");if(f){f.style.height=e.data.height+"px";}}});</script>`;
        const copy = () => { navigator.clipboard.writeText(snippet).then(() => toast({ title: "Embed code copied", description: "Paste it into your website's HTML." })).catch(() => toast({ title: "Copy failed", description: "Select the code and copy manually.", variant: "destructive" })); };
        return (
          <div className="fixed inset-0 z-[80] bg-black/50 flex items-center justify-center p-4" onClick={() => setEmbed(null)} data-testid="embed-modal">
            <div className="bg-white rounded-2xl w-full max-w-[560px] max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
              <div className="flex justify-between items-center p-5 border-b border-gray-100">
                <h3 className="font-bold text-[#16243b] flex items-center gap-2"><Code2 size={18} className="text-[#2ec9c9]" /> Embed this event</h3>
                <button onClick={() => setEmbed(null)} data-testid="embed-close"><X className="text-[#5a6b80]" /></button>
              </div>
              <div className="p-5 space-y-4">
                <p className="text-sm text-[#5a6b80]">Add a live ticket card for <span className="font-semibold text-[#16243b]">{embed.title}</span> to any website. It auto-resizes to fit — no scrollbars inside the frame — and the "Get tickets" button opens your checkout in a new tab.</p>
                <div>
                  <p className="text-xs font-semibold text-[#16243b] uppercase tracking-wide mb-2">Live preview</p>
                  <div className="rounded-xl border border-gray-200 bg-[#f6f8fb] p-4 flex justify-center">
                    <iframe title="Embed preview" src={embedUrl} scrolling="no" data-testid="embed-preview"
                      style={{ width: "100%", maxWidth: 400, height: 340, border: 0, overflow: "hidden" }} />
                  </div>
                </div>
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-semibold text-[#16243b] uppercase tracking-wide">Copy &amp; paste this code</p>
                    <button onClick={copy} data-testid="embed-copy" className="gp-btn-teal rounded-full px-4 py-1.5 text-xs font-medium flex items-center gap-1.5"><Copy size={13} /> Copy code</button>
                  </div>
                  <textarea readOnly value={snippet} data-testid="embed-snippet" onFocus={(e) => e.target.select()}
                    className="w-full h-40 rounded-xl border border-gray-200 bg-[#0f172a] text-[#a7f3d0] font-mono text-[11px] leading-relaxed p-3 outline-none resize-none" />
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {modal && (
        <div className="fixed inset-0 z-[80] bg-black/50 flex items-center justify-center p-4" onClick={() => setModal(null)}>
          <div className="bg-white rounded-2xl w-full max-w-[480px] max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center p-5 border-b border-gray-100">
              <h3 className="font-bold text-[#16243b]">{modal.id ? "Edit event" : "New event"}</h3>
              <button onClick={() => setModal(null)}><X className="text-[#5a6b80]" /></button>
            </div>
            <form onSubmit={save} className="p-5 space-y-4">
              <Field label="Event title" value={modal.title} onChange={(v) => setModal({ ...modal, title: v })} required testid="event-title" />
              <Field label="Short description" value={modal.description} onChange={(v) => setModal({ ...modal, description: v })} textarea rows={4} />
              <div>
                <label className="block text-sm font-medium text-[#16243b] mb-1.5">Date &amp; time</label>
                <input type="datetime-local" value={modal.event_date || ""} onChange={(e) => setModal({ ...modal, event_date: e.target.value })} data-testid="event-date"
                  className="w-full rounded-xl border border-gray-200 px-4 py-2.5 outline-none focus:border-[#2ec9c9]" />
                {!modal.id && modal.event_date && (
                  <div className="mt-2 flex flex-wrap items-center gap-2 text-sm text-[#16243b]" data-testid="repeat-on-create">
                    <span className="text-[#5a6b80]">Repeats</span>
                    <select value={modal.repeat_interval || "weekly"} onChange={(e) => setModal({ ...modal, repeat_interval: e.target.value })} data-testid="repeat-interval-input"
                      className="rounded-lg border border-gray-200 px-2 py-1.5 outline-none focus:border-[#2ec9c9] bg-white">
                      <option value="weekly">weekly</option>
                      <option value="fortnightly">fortnightly</option>
                      <option value="monthly">monthly</option>
                    </select>
                    <span className="text-[#5a6b80]">for</span>
                    <input type="number" min="0" max="52" value={modal.repeat_weeks || ""} onChange={(e) => setModal({ ...modal, repeat_weeks: e.target.value })} data-testid="repeat-weeks-input"
                      placeholder="0" className="w-16 rounded-lg border border-gray-200 px-2 py-1.5 outline-none focus:border-[#2ec9c9]" />
                    <span className="text-[#5a6b80]">extra date(s)</span>
                  </div>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-[#16243b] mb-1.5">End time <span className="text-[#8a97a8] font-normal">(optional)</span></label>
                <input type="datetime-local" value={modal.event_end || ""} onChange={(e) => setModal({ ...modal, event_end: e.target.value })} data-testid="event-end"
                  className="w-full rounded-xl border border-gray-200 px-4 py-2.5 outline-none focus:border-[#2ec9c9]" />
                <p className="text-xs text-[#8a97a8] mt-1">Shown on the event page alongside the start time when set.</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-[#16243b] mb-1.5">City / region</label>
                <select value={modal.city || ""} onChange={(e) => setModal({ ...modal, city: e.target.value })} data-testid="event-city"
                  className="w-full rounded-xl border border-gray-200 px-4 py-2.5 outline-none focus:border-[#2ec9c9] bg-white">
                  <option value="">No city</option>
                  {cities.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                <p className="text-xs text-[#8a97a8] mt-1">Pick the city first, then choose a venue below.</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-[#16243b] mb-1.5">Venue</label>
                {modal.city ? (
                  <>
                    <select value={modal.venue_name || ""} data-testid="event-venue-select"
                      onChange={(e) => {
                        const v = venues.find((x) => x.name === e.target.value);
                        setModal({ ...modal, venue_name: e.target.value, venue_address: v?.address || modal.venue_address });
                      }}
                      className="w-full rounded-xl border border-gray-200 px-4 py-2.5 outline-none focus:border-[#2ec9c9] bg-white">
                      <option value="">Select a venue…</option>
                      {venues.map((v) => <option key={v.id} value={v.name}>{v.name}</option>)}
                    </select>
                    <div className="flex gap-2 mt-2">
                      <input value={newVenue} onChange={(e) => setNewVenue(e.target.value)} data-testid="event-venue-new"
                        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addVenueInline(); } }}
                        placeholder={`Add a new venue in ${modal.city}`}
                        className="flex-1 rounded-xl border border-gray-200 px-4 py-2.5 outline-none focus:border-[#2ec9c9] text-sm" />
                      <button type="button" onClick={addVenueInline} data-testid="event-venue-add"
                        className="gp-btn-teal rounded-xl px-4 py-2.5 text-sm font-medium whitespace-nowrap">Add</button>
                    </div>
                    <p className="text-xs text-[#8a97a8] mt-1">New venues are shared across the platform for everyone in {modal.city}.</p>
                  </>
                ) : (
                  <Field label="" value={modal.venue_name} onChange={(v) => setModal({ ...modal, venue_name: v })} testid="event-venue" />
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-[#16243b] mb-1.5">Event capacity <span className="text-[#8a97a8] font-normal">(optional)</span></label>
                <input type="number" min="0" value={modal.event_capacity || ""} onChange={(e) => setModal({ ...modal, event_capacity: e.target.value })} data-testid="event-capacity"
                  placeholder="0 = no overall cap" className="w-full rounded-xl border border-gray-200 px-4 py-2.5 outline-none focus:border-[#2ec9c9]" />
                {(() => { const v = venues.find((x) => x.name === modal.venue_name); return v?.capacity ? <p className="text-xs text-[#8a97a8] mt-1">{modal.venue_name} holds up to {v.capacity}. Set a lower number to sell fewer for this event.</p> : <p className="text-xs text-[#8a97a8] mt-1">Caps total tickets sold across all ticket types for this event.</p>; })()}
              </div>

              <Field label="Venue address" value={modal.venue_address} onChange={(v) => setModal({ ...modal, venue_address: v })} />
              {modal.venue_name && (
                <label className="flex items-start gap-2.5 rounded-xl border border-gray-200 p-3 cursor-pointer" data-testid="allow-venue-access">
                  <input type="checkbox" checked={!!modal.allow_venue_access} onChange={(e) => setModal({ ...modal, allow_venue_access: e.target.checked })}
                    className="mt-0.5 accent-[#2ec9c9]" data-testid="allow-venue-access-checkbox" />
                  <span className="text-sm text-[#16243b]">Allow <strong>{modal.venue_name}</strong> door staff to check tickets in and sell on the door for this event.
                    <span className="block text-xs text-[#8a97a8] mt-0.5">Only enable if you trust this venue to run your door. They can't edit your event or see analytics.</span></span>
                </label>
              )}
              <div>
                <label className="block text-sm font-medium text-[#16243b] mb-1.5">Category</label>
                <select value={modal.category || ""} onChange={(e) => setModal({ ...modal, category: e.target.value })} data-testid="event-category"
                  className="w-full rounded-xl border border-gray-200 px-4 py-2.5 outline-none focus:border-[#2ec9c9] bg-white">
                  <option value="">No category</option>
                  {categories.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                <p className="text-xs text-[#8a97a8] mt-1">Helps customers find your event when browsing by type on the homepage.</p>
              </div>

              <div data-testid="event-acts">
                <label className="block text-sm font-medium text-[#16243b] mb-1.5">Artists / acts <span className="text-[#8a97a8] font-normal">(optional)</span></label>
                {acts.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-2">
                    {acts.map((a) => {
                      const on = (modal.act_ids || []).includes(a.id);
                      return (
                        <button type="button" key={a.id} onClick={() => toggleAct(a.id)} data-testid={`event-act-${a.id}`}
                          className={`text-sm rounded-full px-3 py-1.5 border transition-colors ${on ? "bg-[#8b5cf6] border-[#8b5cf6] text-white" : "bg-white border-gray-200 text-[#5a6b80] hover:border-[#8b5cf6]"}`}>
                          {a.name}
                        </button>
                      );
                    })}
                  </div>
                )}
                <div className="flex gap-2">
                  <input value={newAct} onChange={(e) => setNewAct(e.target.value)} data-testid="event-act-new"
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addActInline(); } }}
                    placeholder="Add a new artist by name"
                    className="flex-1 rounded-xl border border-gray-200 px-4 py-2.5 outline-none focus:border-[#8b5cf6] text-sm" />
                  <button type="button" onClick={addActInline} data-testid="event-act-add"
                    className="rounded-xl px-4 py-2.5 text-sm font-medium whitespace-nowrap bg-[#8b5cf6] text-white hover:bg-[#7c3aed]">Add</button>
                </div>
                <p className="text-xs text-[#8a97a8] mt-1">Tag the acts performing. Manage your full artist database in the Artists tab.</p>
                {(modal.act_ids || []).length > 1 && (
                  <div className="mt-3" data-testid="act-lineup-order">
                    <p className="text-xs font-medium text-[#16243b] mb-1.5">Line-up order <span className="text-[#8a97a8] font-normal">(drag to reorder — headliner first)</span></p>
                    <div className="space-y-1.5">
                      {(modal.act_ids || []).map((id, idx) => {
                        const a = acts.find((x) => x.id === id);
                        if (!a) return null;
                        return (
                          <div key={id} draggable data-testid={`lineup-item-${id}`}
                            onDragStart={() => setDragIdx(idx)}
                            onDragOver={(e) => e.preventDefault()}
                            onDrop={() => { reorderActs(dragIdx, idx); setDragIdx(null); }}
                            className={`flex items-center gap-2 rounded-lg border px-3 py-2 bg-white cursor-move ${dragIdx === idx ? "border-[#8b5cf6] opacity-60" : "border-gray-200"}`}>
                            <GripVertical size={15} className="text-[#c3ccd8]" />
                            <span className="text-xs font-semibold text-[#8b5cf6] w-5">{idx + 1}</span>
                            <span className="text-sm text-[#16243b] flex-1 truncate">{a.name}</span>
                            {idx === 0 && <span className="text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full bg-[#8b5cf6]/10 text-[#7c3aed]">Headliner</span>}
                            <button type="button" onClick={() => toggleAct(id)} data-testid={`lineup-remove-${id}`} className="text-[#c3ccd8] hover:text-red-500"><X size={14} /></button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>


              {rosterArtists.length > 0 && (
                <div data-testid="event-lineup">
                  <label className="block text-sm font-medium text-[#16243b] mb-1.5">Networked lineup <span className="text-[#8a97a8] font-normal">(shown on the public event page, links to their profile)</span></label>
                  <div className="flex flex-wrap gap-2">
                    {rosterArtists.map((a) => {
                      const on = (modal.lineup_artist_ids || []).includes(a.id);
                      return (
                        <button type="button" key={a.id} onClick={() => toggleLineup(a.id)} data-testid={`event-lineup-${a.id}`}
                          className={`text-sm rounded-full px-3 py-1.5 border transition-colors ${on ? "bg-[#2ec9c9] border-[#2ec9c9] text-white" : "bg-white border-gray-200 text-[#5a6b80] hover:border-[#2ec9c9]"}`}>
                          {a.name}
                        </button>
                      );
                    })}
                  </div>
                  <p className="text-xs text-[#8a97a8] mt-1">Pick artists from your roster (Roster &amp; bookings tab) to feature on the storefront so fans can explore the lineup.</p>
                  {(modal.lineup_artist_ids || []).length > 1 && (
                    <div className="mt-3" data-testid="lineup-order">
                      <p className="text-xs font-medium text-[#16243b] mb-1.5">Headliner order <span className="text-[#8a97a8] font-normal">(drag to reorder — headliner first)</span></p>
                      <div className="space-y-1.5">
                        {(modal.lineup_artist_ids || []).map((id, idx) => {
                          const a = rosterArtists.find((x) => x.id === id);
                          if (!a) return null;
                          return (
                            <div key={id} draggable data-testid={`lineup-order-item-${id}`}
                              onDragStart={() => setLineupDragIdx(idx)}
                              onDragOver={(e) => e.preventDefault()}
                              onDrop={() => { reorderLineup(lineupDragIdx, idx); setLineupDragIdx(null); }}
                              className={`flex items-center gap-2 rounded-lg border px-3 py-2 bg-white cursor-move ${lineupDragIdx === idx ? "border-[#2ec9c9] opacity-60" : "border-gray-200"}`}>
                              <GripVertical size={15} className="text-[#c3ccd8]" />
                              <span className="text-xs font-semibold text-[#1fb0b0] w-5">{idx + 1}</span>
                              <span className="text-sm text-[#16243b] flex-1 truncate">{a.name}</span>
                              {idx === 0 && <span className="text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full bg-[#2ec9c9]/10 text-[#1fb0b0]">Headliner</span>}
                              <input type="time" value={(modal.lineup_set_times || {})[id] || ""} data-testid={`lineup-time-${id}`}
                                onChange={(e) => setModal((m) => ({ ...m, lineup_set_times: { ...(m.lineup_set_times || {}), [id]: e.target.value } }))}
                                title="Set / stage time" className="rounded-lg border border-gray-200 px-2 py-1 text-xs outline-none focus:border-[#2ec9c9]" />
                              <button type="button" onClick={() => toggleLineup(id)} data-testid={`lineup-order-remove-${id}`} className="text-[#c3ccd8] hover:text-red-500"><X size={14} /></button>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}


              {/* Ticket tiers */}
              <div className="border-t border-gray-100 pt-4">
                <label className="flex items-start gap-2.5 rounded-xl border border-gray-200 p-3 cursor-pointer mb-3" data-testid="reserved-seating">
                  <input type="checkbox" checked={!!modal.reserved_seating} onChange={(e) => setModal({ ...modal, reserved_seating: e.target.checked })}
                    className="mt-0.5 accent-[#2ec9c9]" data-testid="reserved-seating-checkbox" />
                  <span className="text-sm text-[#16243b]">Reserved seating — buyers pick a specific seat
                    <span className="block text-xs text-[#8a97a8] mt-0.5">Off = general admission (first come, best seats). On = give each ticket type a seat grid below; capacity becomes rows × seats.</span></span>
                </label>
                <label className="flex items-start gap-2.5 rounded-xl border border-gray-200 p-3 cursor-pointer mb-3" data-testid="tickets-disabled">
                  <input type="checkbox" checked={!!modal.tickets_disabled} onChange={(e) => setModal({ ...modal, tickets_disabled: e.target.checked })}
                    className="mt-0.5 accent-[#2ec9c9]" data-testid="tickets-disabled-checkbox" />
                  <span className="text-sm text-[#16243b]">No tickets — info-only event
                    <span className="block text-xs text-[#8a97a8] mt-0.5">On = the storefront shows the event details but hides ticket selection &amp; checkout (great for free "just turn up" events). Ticket types below are ignored while this is on.</span></span>
                </label>
                <label className="block text-sm font-medium text-[#16243b] mb-2">Ticket types</label>
                <div className="space-y-2">
                  {(modal.ticket_tiers || []).map((t, i) => (
                    <div key={i} data-testid={`tier-row-${i}`} className="space-y-1.5">
                      <div className="flex gap-2 items-center">
                        <input placeholder="Name (e.g. VIP)" value={t.name} onChange={(e) => setTier(i, "name", e.target.value)} data-testid={`tier-name-${i}`}
                          className="flex-1 rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-[#2ec9c9]" />
                        {t.free ? (
                          <span className="w-20 text-center text-sm font-semibold text-green-600 py-2 rounded-lg bg-green-50 border border-green-200" data-testid={`tier-free-label-${i}`}>Free</span>
                        ) : (
                          <input type="number" step="0.01" placeholder="£" value={t.price} onChange={(e) => setTier(i, "price", e.target.value)} data-testid={`tier-price-${i}`}
                            className="w-20 rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-[#2ec9c9]" />
                        )}
                        <label className="flex items-center gap-1 text-xs text-[#5a6b80] cursor-pointer whitespace-nowrap" title="Free ticket — no payment, attendee gets a QR ticket">
                          <input type="checkbox" checked={!!t.free} onChange={(e) => setTier(i, "free", e.target.checked)} data-testid={`tier-free-${i}`} className="accent-[#2ec9c9]" />
                          Free
                        </label>
                        {!modal.reserved_seating && (
                          <input type="number" placeholder="Qty" value={t.capacity} onChange={(e) => setTier(i, "capacity", e.target.value)} title="Capacity (0 = unlimited)" data-testid={`tier-cap-${i}`}
                            className="w-16 rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-[#2ec9c9]" />
                        )}
                        <button type="button" onClick={() => rmTier(i)} className="text-red-400 hover:text-red-600 p-1"><Trash2 size={15} /></button>
                      </div>
                      {modal.reserved_seating && (
                        <div className="pl-1 space-y-2 bg-[#f9fbfd] rounded-lg p-2.5 border border-gray-100">
                          <label className="flex items-center gap-2 text-xs text-[#16243b]">
                            <input type="checkbox" checked={!!t.table_mode} onChange={(e) => setTier(i, "table_mode", e.target.checked)} data-testid={`tier-tablemode-${i}`} className="accent-[#2ec9c9]" />
                            Round tables (galas/dinners)
                          </label>
                          {t.table_mode ? (
                            <div className="flex gap-2 items-center flex-wrap text-xs text-[#8a97a8]">
                              <span>Tables:</span>
                              <input type="number" min="0" placeholder="Tables" value={t.tables || ""} onChange={(e) => setTier(i, "tables", e.target.value)} data-testid={`tier-tables-${i}`}
                                className="w-16 rounded-lg border border-gray-200 px-2 py-1.5 text-sm outline-none focus:border-[#2ec9c9]" />
                              <span>×</span>
                              <input type="number" min="0" placeholder="Seats" value={t.seats_per_table || ""} onChange={(e) => setTier(i, "seats_per_table", e.target.value)} data-testid={`tier-spt-${i}`}
                                className="w-16 rounded-lg border border-gray-200 px-2 py-1.5 text-sm outline-none focus:border-[#2ec9c9]" />
                              <span>seats/table</span>
                              <label className="flex items-center gap-1.5 ml-1">Colour
                                <input type="color" value={t.section_color || SECTION_PALETTE[i % SECTION_PALETTE.length]} onChange={(e) => setTier(i, "section_color", e.target.value)} data-testid={`tier-color-${i}`}
                                  className="w-7 h-7 rounded border border-gray-200 cursor-pointer p-0" /></label>
                              <span className="text-[#16243b] font-medium">= {(parseInt(t.tables) || 0) * (parseInt(t.seats_per_table) || 0)} seats</span>
                            </div>
                          ) : null}
                          {t.table_mode && (
                            <div className="flex gap-2 items-center flex-wrap text-xs text-[#8a97a8]">
                              <label className="flex items-center gap-1.5">Whole-table discount
                                <input type="number" min="0" max="100" step="1" placeholder="0" value={t.table_discount_pct || ""} onChange={(e) => setTier(i, "table_discount_pct", e.target.value)} data-testid={`tier-tablediscount-${i}`}
                                  className="w-14 rounded-lg border border-gray-200 px-2 py-1.5 text-sm outline-none focus:border-[#2ec9c9]" /> %</label>
                              <span className="text-[#8a97a8]">off when a host books a full table</span>
                            </div>
                          )}
                          {!t.table_mode && (
                          <>
                          <div className="flex gap-2 items-center flex-wrap text-xs text-[#8a97a8]">
                            <span>Grid:</span>
                            <input type="number" min="0" placeholder="Rows" value={t.seat_rows || ""} onChange={(e) => setTier(i, "seat_rows", e.target.value)} data-testid={`tier-rows-${i}`}
                              className="w-16 rounded-lg border border-gray-200 px-2 py-1.5 text-sm outline-none focus:border-[#2ec9c9]" />
                            <span>×</span>
                            <input type="number" min="0" placeholder="Per row" value={t.seat_cols || ""} onChange={(e) => setTier(i, "seat_cols", e.target.value)} data-testid={`tier-cols-${i}`}
                              className="w-16 rounded-lg border border-gray-200 px-2 py-1.5 text-sm outline-none focus:border-[#2ec9c9]" />
                            <label className="flex items-center gap-1.5 ml-1">Colour
                              <input type="color" value={t.section_color || SECTION_PALETTE[i % SECTION_PALETTE.length]} onChange={(e) => setTier(i, "section_color", e.target.value)} data-testid={`tier-color-${i}`}
                                className="w-7 h-7 rounded border border-gray-200 cursor-pointer p-0" /></label>
                          </div>
                          <div className="flex gap-2 items-center flex-wrap text-xs text-[#8a97a8]">
                            <label className="flex items-center gap-1.5">Aisle after seats
                              <input placeholder="e.g. 4, 8" value={t.aisleStr || ""} onChange={(e) => setTier(i, "aisleStr", e.target.value)} data-testid={`tier-aisle-${i}`}
                                className="w-24 rounded-lg border border-gray-200 px-2 py-1.5 text-sm outline-none focus:border-[#2ec9c9]" /></label>
                            <label className="flex items-center gap-1.5">Custom rows (overrides grid)
                              <input placeholder="e.g. 10,10,12,14" value={t.rowSeatsStr || ""} onChange={(e) => setTier(i, "rowSeatsStr", e.target.value)} data-testid={`tier-rowseats-${i}`}
                                className="w-32 rounded-lg border border-gray-200 px-2 py-1.5 text-sm outline-none focus:border-[#2ec9c9]" /></label>
                          </div>
                          <p className="text-[11px] text-[#16243b] font-medium">= {parseCSVInts(t.rowSeatsStr).length ? parseCSVInts(t.rowSeatsStr).reduce((a, b) => a + b, 0) : (parseInt(t.seat_rows) || 0) * (parseInt(t.seat_cols) || 0)} seats</p>
                          </>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                  <button type="button" onClick={addTier} data-testid="add-tier-btn" className="text-[#1fb0b0] text-sm font-medium flex items-center gap-1"><Plus size={14} /> Add ticket type</button>
                  <p className="text-xs text-[#8a97a8]">{modal.reserved_seating ? "Seats are auto-labelled A1, A2… by row." : "Capacity 0 = unlimited."}</p>
                </div>

                {modal.reserved_seating && (
                  <div className="mt-4 border-t border-gray-100 pt-4 space-y-3" data-testid="seat-designer">
                    <div className="flex gap-3 items-center flex-wrap">
                      <label className="text-sm font-medium text-[#16243b]">Stage marker</label>
                      <input placeholder="STAGE" value={modal.seat_stage_label ?? "STAGE"} onChange={(e) => setModal({ ...modal, seat_stage_label: e.target.value })} data-testid="stage-label"
                        className="w-32 rounded-lg border border-gray-200 px-3 py-1.5 text-sm outline-none focus:border-[#2ec9c9]" />
                      <select value={modal.seat_stage_position || "top"} onChange={(e) => setModal({ ...modal, seat_stage_position: e.target.value })} data-testid="stage-position"
                        className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm outline-none focus:border-[#2ec9c9] bg-white">
                        <option value="top">At top</option>
                        <option value="bottom">At bottom</option>
                      </select>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-[#16243b] mb-2">Live preview</p>
                      <SeatMap sections={tiersToSections((modal.ticket_tiers || []).map((t) => ({ ...t, row_seats: parseCSVInts(t.rowSeatsStr), aisle_after: parseCSVInts(t.aisleStr) })))}
                        stageLabel={modal.seat_stage_label ?? "STAGE"} stagePosition={modal.seat_stage_position || "top"} interactive={false} />
                    </div>
                  </div>
                )}
              </div>

              <ImageUpload label="Main image" testid="event-image-url" value={modal.image_url} onChange={(v) => setModal({ ...modal, image_url: v })} />
              <div>
                <label className="block text-sm font-medium text-[#16243b] mb-1.5">Gallery images</label>
                <div className="space-y-2">
                  {(modal.gallery_images || []).map((g, i) => (
                    <ImageUpload key={i} value={g} compact onChange={(v) => setModal({ ...modal, gallery_images: modal.gallery_images.map((x, xi) => (xi === i ? v : x)) })} />
                  ))}
                  <button type="button" onClick={() => setModal({ ...modal, gallery_images: [...(modal.gallery_images || []), ""] })} className="text-[#1fb0b0] text-sm font-medium flex items-center gap-1"><Plus size={14} /> Add gallery image</button>
                </div>
              </div>
              <Field label="Full description (event page)" value={modal.long_description} onChange={(v) => setModal({ ...modal, long_description: v })} textarea rows={7} />
              <Field label="Terms & conditions" value={modal.terms} onChange={(v) => setModal({ ...modal, terms: v })} textarea />

              <div className="border-t border-gray-100 pt-4">
                <h4 className="font-semibold text-[#16243b] text-sm mb-1">SEO & social sharing</h4>
                <p className="text-xs text-[#8a97a8] mb-3">Sets this event page's title, search description and link-preview image.</p>
                <div className="space-y-4">
                  <Field label="SEO page title" value={modal.seo_title} onChange={(v) => setModal({ ...modal, seo_title: v })} placeholder={modal.title} testid="product-seo-title" />
                  <Field label="SEO / share description" value={modal.seo_description} onChange={(v) => setModal({ ...modal, seo_description: v })} textarea placeholder={modal.description} testid="product-seo-description" />
                  <ImageUpload label="Social share image" value={modal.share_image} onChange={(v) => setModal({ ...modal, share_image: v })} />
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm text-[#16243b]">
                <input type="checkbox" checked={modal.active} onChange={(e) => setModal({ ...modal, active: e.target.checked })} /> Active (visible to buyers)
              </label>
              <label className="flex items-start gap-2.5 rounded-xl border border-gray-200 p-3 cursor-pointer" data-testid="sold-out-toggle">
                <input type="checkbox" checked={!!modal.sold_out} onChange={(e) => setModal({ ...modal, sold_out: e.target.checked })}
                  className="mt-0.5 accent-[#ef4444]" data-testid="sold-out-checkbox" />
                <span className="text-sm text-[#16243b]">Mark as sold out
                  <span className="block text-xs text-[#8a97a8] mt-0.5">Event stays visible but ticket sales are turned off. Buyers can still join the waitlist.</span></span>
              </label>
              <label className="flex items-start gap-2.5 rounded-xl border border-gray-200 p-3 cursor-pointer" data-testid="show-sold-bar-toggle">
                <input type="checkbox" checked={!!modal.show_sold_bar} onChange={(e) => setModal({ ...modal, show_sold_bar: e.target.checked })}
                  className="mt-0.5 accent-[#2ec9c9]" data-testid="show-sold-bar-checkbox" />
                <span className="text-sm text-[#16243b]">Show a "% sold" progress bar
                  <span className="block text-xs text-[#8a97a8] mt-0.5">On = buyers see a bar with how much of the event has sold (e.g. 80% sold), adding urgency. Needs a set capacity to work.</span></span>
              </label>
              <label className="flex items-start gap-2.5 rounded-xl border border-gray-200 p-3 cursor-pointer" data-testid="private-event-toggle">
                <input type="checkbox" checked={!!modal.private} onChange={(e) => setModal({ ...modal, private: e.target.checked })}
                  className="mt-0.5 accent-[#2ec9c9]" data-testid="private-event-checkbox" />
                <span className="text-sm text-[#16243b]">Private event
                  <span className="block text-xs text-[#8a97a8] mt-0.5">Hidden from the homepage, discovery and your store page — reachable only by direct link. Still live in your hub.</span></span>
              </label>
              {modal.private && (
                <div data-testid="private-password-field">
                  <label className="block text-sm font-medium text-[#16243b] mb-1.5">Password protection <span className="text-[#8a97a8] font-normal">(optional)</span></label>
                  <input type="text" value={modal.access_password || ""} onChange={(e) => setModal({ ...modal, access_password: e.target.value })}
                    placeholder="Leave blank for no password" data-testid="private-password-input"
                    className="w-full rounded-xl border border-gray-200 px-4 py-2.5 outline-none focus:border-[#2ec9c9] text-sm" />
                  <p className="text-xs text-[#8a97a8] mt-1">If set, visitors must enter this password to see tickets and check out.</p>
                </div>
              )}
              <label className="flex items-start gap-2.5 rounded-xl border border-gray-200 p-3 cursor-pointer" data-testid="dinner-toggle">
                <input type="checkbox" checked={!!modal.is_dinner} onChange={(e) => setModal({ ...modal, is_dinner: e.target.checked })}
                  className="mt-0.5 accent-[#2ec9c9]" data-testid="dinner-checkbox" />
                <span className="text-sm text-[#16243b] flex items-center gap-1.5"><Utensils size={14} className="text-[#2ec9c9]" /> This event includes a dinner / food
                  <span className="block text-xs text-[#8a97a8] mt-0.5">Adds a downloadable food menu to the event page, kept separate from the entertainment line-up.</span></span>
              </label>
              {modal.is_dinner && (
                <div className="rounded-xl border border-gray-200 p-4" data-testid="food-menu-field">
                  <label className="block text-sm font-medium text-[#16243b] mb-1.5">Food menu (PDF)</label>
                  {modal.food_menu_url ? (
                    <div className="flex items-center justify-between rounded-lg bg-[#f6f8fb] px-3 py-2.5 mb-2">
                      <a href={modal.food_menu_url} target="_blank" rel="noopener noreferrer" data-testid="food-menu-view" className="text-sm text-[#1fb0b0] font-medium flex items-center gap-1.5 truncate"><FileText size={15} /> View uploaded menu</a>
                      <button type="button" onClick={() => setModal({ ...modal, food_menu_url: "" })} data-testid="food-menu-remove" className="text-[#8a97a8] hover:text-red-500 flex-shrink-0 ml-2"><Trash2 size={15} /></button>
                    </div>
                  ) : null}
                  <label className="inline-flex items-center gap-2 cursor-pointer gp-btn-outline rounded-full px-4 py-2 text-xs font-medium">
                    {menuUploading ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} {modal.food_menu_url ? "Replace PDF" : "Upload PDF menu"}
                    <input type="file" accept="application/pdf,.pdf" className="hidden" data-testid="food-menu-input"
                      onChange={(e) => { uploadFoodMenu(e.target.files[0]); e.target.value = ""; }} />
                  </label>
                  <p className="text-xs text-[#8a97a8] mt-2">Guests can view or download this menu from the event page. Max 15MB.</p>
                </div>
              )}
              <button className="gp-btn-teal w-full rounded-xl py-3 font-medium" data-testid="save-event-btn">Save event</button>
            </form>
          </div>
        </div>
      )}

      {heatmap && (
        <div className="fixed inset-0 z-[70] bg-black/50 flex items-center justify-center p-4" onClick={() => setHeatmap(null)} data-testid="heatmap-modal">
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[88vh] overflow-auto p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-1">
              <h3 className="font-semibold text-[#16243b] flex items-center gap-2"><Flame size={18} className="text-[#ef4444]" /> Seat sales heatmap</h3>
              <button onClick={() => setHeatmap(null)} className="text-[#8a97a8] hover:text-[#16243b]"><X size={18} /></button>
            </div>
            <p className="text-sm text-[#8a97a8] mb-4">{heatmap.event.title} — red seats are sold. Use the section percentages to spot which areas sell fastest and price accordingly.</p>
            {!heatmap.stats ? (
              <div className="py-12 flex justify-center"><Loader2 className="animate-spin text-[#2ec9c9]" /></div>
            ) : (
              <SeatMap variant="heatmap" interactive={false}
                sections={availToSections({ ticket_tiers: heatmap.stats.ticket_tiers, seat_availability: heatmap.stats.seat_availability })}
                stageLabel={heatmap.stats.seat_stage_label} stagePosition={heatmap.stats.seat_stage_position || "top"} />
            )}
          </div>
        </div>
      )}

      {promoResults !== null && (
        <div className="fixed inset-0 z-[80] bg-black/50 flex items-center justify-center p-4" onClick={() => setPromoResults(null)} data-testid="promo-results-modal">
          <div className="bg-white rounded-2xl w-full max-w-2xl p-6 max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-[#16243b] text-lg flex items-center gap-2"><Send size={18} /> Promo results</h3>
              <button onClick={() => setPromoResults(null)}><X size={18} className="text-[#8a97a8]" /></button>
            </div>
            {promoResults.length === 0 ? (
              <p className="text-sm text-[#8a97a8] py-8 text-center">No promos sent yet. Use the "Promote" button on any event to cross-sell another show.</p>
            ) : (
              <div className="space-y-3">
                {promoResults.map((c) => (
                  <div key={c.id} className="rounded-xl border border-gray-100 p-4" data-testid={`promo-result-${c.id}`}>
                    <p className="font-semibold text-[#16243b]">{c.target_title}</p>
                    <p className="text-xs text-[#8a97a8] mb-2">{c.subject} · {fmtDate(c.created_at)}</p>
                    <div className="grid grid-cols-5 gap-2 text-center">
                      {[["Sent", c.sent], ["Opens", c.opens], ["Clicks", c.clicks], ["Tickets", c.tickets_driven], ["Revenue", `£${(c.revenue_driven || 0).toFixed(2)}`]].map(([k, v]) => (
                        <div key={k}><p className="text-base font-bold text-[#16243b]">{v == null ? "—" : v}</p><p className="text-[10px] text-[#8a97a8] uppercase">{k}</p></div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}


      {promo && (
        <div className="fixed inset-0 z-[80] bg-black/50 flex items-center justify-center p-4" onClick={() => !promo.busy && setPromo(null)} data-testid="promote-modal">
          <div className="bg-white rounded-2xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-1">
              <h3 className="font-semibold text-[#16243b] text-lg flex items-center gap-2"><Send size={18} /> Promote another show</h3>
              <button onClick={() => setPromo(null)}><X size={18} className="text-[#8a97a8]" /></button>
            </div>
            <p className="text-sm text-[#8a97a8] mb-4">Email ticket-holders of <strong className="text-[#5a6b80]">{promo.source.title}</strong>{promo.audience != null ? ` (${promo.audience} recipient${promo.audience !== 1 ? "s" : ""})` : ""} about another show.</p>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-medium text-[#8a97a8] mb-1.5 block">Show to promote</label>
                <select value={promo.target_event_id} onChange={(e) => setPromo({ ...promo, target_event_id: e.target.value })} data-testid="promote-target-select"
                  className="w-full rounded-xl border border-gray-200 px-4 py-2.5 outline-none focus:border-[#2ec9c9] text-sm">
                  <option value="">Choose a show…</option>
                  {events.filter((e) => e.id !== promo.source.id && !e.cancelled && !e.archived).map((e) => (
                    <option key={e.id} value={e.id}>{e.title}{e.event_date ? ` — ${fmtDate(e.event_date)}` : ""}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-[#8a97a8] mb-1.5 block">Subject <span className="text-[#b6c0cd]">(optional)</span></label>
                <input value={promo.subject} onChange={(e) => setPromo({ ...promo, subject: e.target.value })} data-testid="promote-subject" placeholder="e.g. Staying out? Join us at the 8pm show"
                  className="w-full rounded-xl border border-gray-200 px-4 py-2.5 outline-none focus:border-[#2ec9c9] text-sm" />
              </div>
              <div>
                <label className="text-xs font-medium text-[#8a97a8] mb-1.5 block">Your message</label>
                <textarea value={promo.message} onChange={(e) => setPromo({ ...promo, message: e.target.value })} data-testid="promote-message" rows={4}
                  placeholder="Loved the 7pm show? We've got an 8pm set the same night — grab a seat before they're gone!"
                  className="w-full rounded-xl border border-gray-200 px-4 py-2.5 outline-none focus:border-[#2ec9c9] text-sm resize-none" />
              </div>
              <div>
                <label className="text-xs font-medium text-[#8a97a8] mb-1.5 block">Feature a promo code <span className="text-[#b6c0cd]">(optional)</span></label>
                <select value={promo.discount_code} onChange={(e) => setPromo({ ...promo, discount_code: e.target.value })} data-testid="promote-discount-select"
                  className="w-full rounded-xl border border-gray-200 px-4 py-2.5 outline-none focus:border-[#2ec9c9] text-sm">
                  <option value="">No code</option>
                  {discounts.map((d) => <option key={d.id} value={d.code}>{d.code}</option>)}
                </select>
              </div>
              <label className="flex items-center gap-2 text-sm text-[#5a6b80] cursor-pointer">
                <input type="checkbox" checked={promo.include_details} onChange={(e) => setPromo({ ...promo, include_details: e.target.checked })} data-testid="promote-include-details" className="w-4 h-4 accent-[#2ec9c9]" />
                Include the show's date, venue &amp; price block
              </label>
              <label className="flex items-center gap-2 text-sm text-[#5a6b80] cursor-pointer">
                <input type="checkbox" checked={promo.checked_in_only} onChange={(e) => setPromo({ ...promo, checked_in_only: e.target.checked })} data-testid="promote-checkedin-only" className="w-4 h-4 accent-[#2ec9c9]" />
                Only those already checked in to {promo.source.title}
              </label>
              <label className="flex items-center gap-2 text-sm text-[#5a6b80] cursor-pointer">
                <input type="checkbox" checked={promo.exclude_target_buyers} onChange={(e) => setPromo({ ...promo, exclude_target_buyers: e.target.checked })} data-testid="promote-exclude-buyers" className="w-4 h-4 accent-[#2ec9c9]" />
                Exclude anyone who already bought the promoted show
              </label>
              <div>
                <label className="text-xs font-medium text-[#8a97a8] mb-1.5 block">Also email holders of <span className="text-[#b6c0cd]">(optional)</span></label>
                <div className="max-h-28 overflow-y-auto border border-gray-100 rounded-xl p-2 space-y-1">
                  {events.filter((e) => e.id !== promo.source.id && e.id !== promo.target_event_id).map((e) => (
                    <label key={e.id} className="flex items-center gap-2 px-2 py-1 rounded-lg hover:bg-gray-50 cursor-pointer text-sm text-[#16243b]">
                      <input type="checkbox" checked={promo.source_event_ids.includes(e.id)} data-testid={`promote-extra-source-${e.id}`}
                        onChange={(ev) => setPromo({ ...promo, source_event_ids: ev.target.checked ? [...promo.source_event_ids, e.id] : promo.source_event_ids.filter((x) => x !== e.id) })}
                        className="w-4 h-4 accent-[#2ec9c9]" />
                      {e.title}
                    </label>
                  ))}
                  {events.filter((e) => e.id !== promo.source.id && e.id !== promo.target_event_id).length === 0 && <p className="text-xs text-[#8a97a8] px-2 py-2">No other shows.</p>}
                </div>
              </div>
            </div>
            <div className="flex items-center justify-between gap-2 mt-5">
              <button onClick={() => sendPromo(true)} disabled={promo.busy || !promo.target_event_id || !promo.message.trim()} data-testid="promote-send-test"
                className="text-sm text-[#1fb0b0] font-medium hover:underline disabled:opacity-40 disabled:no-underline">Send test to me</button>
              <div className="flex gap-2">
                <button onClick={() => setPromo(null)} className="gp-btn-outline rounded-xl px-5 py-2.5 text-sm font-medium">Cancel</button>
                <button onClick={() => sendPromo(false)} disabled={promo.busy || !promo.target_event_id || !promo.message.trim()} data-testid="promote-send"
                  className="gp-btn-teal rounded-xl px-6 py-2.5 text-sm font-medium disabled:opacity-60 flex items-center gap-2">
                  {promo.busy ? <Loader2 className="animate-spin" size={15} /> : <Send size={15} />} Send to {promo.audience != null ? promo.audience : ""} holder{promo.audience === 1 ? "" : "s"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}


      {staffModal && (
        <div className="fixed inset-0 z-[80] bg-black/50 flex items-center justify-center p-4" onClick={() => !staffModal.busy && setStaffModal(null)} data-testid="assign-staff-modal">
          <div className="bg-white rounded-2xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-1">
              <h3 className="font-semibold text-[#16243b] text-lg flex items-center gap-2"><UserCog size={18} /> Assign door staff</h3>
              <button onClick={() => setStaffModal(null)}><X size={18} className="text-[#8a97a8]" /></button>
            </div>
            <p className="text-sm text-[#8a97a8] mb-4 truncate">{staffModal.event.title}</p>
            {staffModal.staff === null ? (
              <div className="py-10 flex justify-center"><Loader2 className="animate-spin text-[#2ec9c9]" size={24} /></div>
            ) : staffModal.staff.length === 0 ? (
              <p className="text-sm text-[#8a97a8] py-6 text-center">No door staff yet. Add staff logins on the <strong>Door staff</strong> tab first.</p>
            ) : (
              <div className="max-h-72 overflow-y-auto border border-gray-100 rounded-xl p-2 space-y-1">
                {staffModal.staff.map((s) => (
                  <label key={s.id} className="flex items-center gap-2.5 px-2 py-2 rounded-lg hover:bg-gray-50 cursor-pointer">
                    <input type="checkbox" checked={staffModal.selected.has(s.id)} data-testid={`assign-staff-opt-${s.id}`}
                      onChange={(e) => {
                        const sel = new Set(staffModal.selected);
                        e.target.checked ? sel.add(s.id) : sel.delete(s.id);
                        setStaffModal({ ...staffModal, selected: sel });
                      }} className="w-4 h-4 accent-[#2ec9c9]" />
                    <span className="text-sm text-[#16243b] leading-tight">{s.name}<span className="block text-xs text-[#8a97a8]">{s.email}</span></span>
                  </label>
                ))}
              </div>
            )}
            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => setStaffModal(null)} className="gp-btn-outline rounded-xl px-5 py-2.5 text-sm font-medium">Cancel</button>
              <button onClick={saveEventStaff} disabled={staffModal.busy || staffModal.staff === null || staffModal.staff.length === 0} data-testid="assign-staff-save" className="gp-btn-teal rounded-xl px-6 py-2.5 text-sm font-medium disabled:opacity-60 flex items-center gap-2">{staffModal.busy ? <Loader2 className="animate-spin" size={15} /> : <CheckCircle2 size={15} />} Save</button>
            </div>
          </div>
        </div>
      )}


      {restoreModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setRestoreModal(null)}>
          <div className="bg-white rounded-2xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()} data-testid="restore-modal">
            <div className="flex items-center gap-2 mb-1"><RotateCcw size={18} className="text-[#2ec9c9]" /><h3 className="font-semibold text-[#16243b] text-lg">Restore "{restoreModal.title}"</h3></div>
            <p className="text-sm text-[#8a97a8] mb-4">This event's date has passed, so it would be auto-archived again straight away. Set a fresh date to put the postponed show back on sale.</p>
            <label className="block text-sm font-medium text-[#16243b] mb-1.5">New event date &amp; time</label>
            <input type="datetime-local" value={(restoreDate || "").slice(0, 16)} onChange={(e) => setRestoreDate(e.target.value)} data-testid="restore-date"
              className="w-full rounded-xl border border-gray-200 px-4 py-2.5 outline-none focus:border-[#2ec9c9]" />
            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => setRestoreModal(null)} className="gp-btn-outline rounded-xl px-5 py-2.5 text-sm font-medium">Cancel</button>
              <button onClick={() => doRestore(restoreModal.id, restoreDate)} disabled={!restoreDate} data-testid="restore-confirm"
                className="gp-btn-teal rounded-xl px-6 py-2.5 text-sm font-medium disabled:opacity-60">Restore &amp; publish</button>
            </div>
          </div>
        </div>
      )}

      {cancelModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setCancelModal(null)}>
          <div className="bg-white rounded-2xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()} data-testid="cancel-modal">
            <div className="flex items-center gap-2 mb-1"><Ban size={18} className="text-[#c0392b]" /><h3 className="font-semibold text-[#16243b] text-lg">Cancel "{cancelModal.title}"</h3></div>
            <p className="text-sm text-[#8a97a8] mb-4">Visitors will see a cancellation notice and can no longer buy tickets.</p>
            <label className="block text-sm font-medium text-[#16243b] mb-1.5">Message to show / email (optional)</label>
            <textarea value={cancelMsg} onChange={(e) => setCancelMsg(e.target.value)} data-testid="cancel-message" rows={2}
              placeholder="e.g. Rescheduled to 15 Dec — hold onto your tickets."
              className="w-full rounded-xl border border-gray-200 px-4 py-2.5 outline-none focus:border-[#2ec9c9] text-sm" />
            <label className="flex items-center gap-2.5 mt-4 cursor-pointer">
              <input type="checkbox" checked={cancelRefund} onChange={(e) => setCancelRefund(e.target.checked)} data-testid="cancel-auto-refund" className="w-4 h-4 accent-[#2ec9c9]" />
              <span className="text-sm text-[#16243b]">Automatically refund all ticket holders</span>
            </label>
            <label className="flex items-center gap-2.5 mt-2.5 cursor-pointer">
              <input type="checkbox" checked={cancelNotify} onChange={(e) => setCancelNotify(e.target.checked)} data-testid="cancel-notify" className="w-4 h-4 accent-[#2ec9c9]" />
              <span className="text-sm text-[#16243b]">Email all ticket holders about the cancellation</span>
            </label>
            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => setCancelModal(null)} className="gp-btn-outline rounded-xl px-5 py-2.5 text-sm font-medium">Back</button>
              <button onClick={confirmCancel} disabled={cancelBusy} data-testid="cancel-confirm"
                className="rounded-xl px-6 py-2.5 text-sm font-medium bg-[#c0392b] text-white hover:bg-[#a5352c] disabled:opacity-60 flex items-center gap-2">
                {cancelBusy ? <Loader2 className="animate-spin" size={15} /> : <Ban size={15} />} Cancel event
              </button>
            </div>
          </div>
        </div>
      )}

      {cancelResult && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setCancelResult(null)}>
          <div className="bg-white rounded-2xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()} data-testid="refund-result-modal">
            <h3 className="font-semibold text-[#16243b] text-lg mb-1">Refund summary</h3>
            <p className="text-sm text-[#8a97a8] mb-4">"{cancelResult.title}" has been cancelled.</p>
            <div className="rounded-xl bg-[#f6f8fb] p-4 flex items-center gap-3" data-testid="refund-progress">
              <CheckCircle2 className={cancelResult.failed_tickets ? "text-amber-500" : "text-[#2ec9c9]"} size={28} />
              <div>
                <p className="text-lg font-bold text-[#16243b]">{cancelResult.refunded_tickets} of {cancelResult.total_tickets} tickets refunded</p>
                <p className="text-xs text-[#8a97a8]">£{(cancelResult.refunded_amount || 0).toFixed(2)} refunded · {cancelResult.emails_sent} email(s) sent</p>
              </div>
            </div>
            {cancelResult.failed_tickets > 0 && (
              <div className="mt-3 rounded-xl border border-amber-200 bg-[#fff7ed] p-3" data-testid="refund-failures">
                <p className="text-sm font-semibold text-[#b45309] flex items-center gap-1.5"><AlertTriangle size={15} /> {cancelResult.failed_tickets} refund(s) failed</p>
                <ul className="mt-1.5 space-y-1 max-h-40 overflow-y-auto">
                  {cancelResult.failures.map((f, i) => (
                    <li key={i} className="text-xs text-[#92400e]"><span className="font-mono">{f.code}</span> — {f.reason}</li>
                  ))}
                </ul>
                <p className="text-[11px] text-[#8a97a8] mt-2">Refund these manually from the order, or via your Stripe dashboard.</p>
              </div>
            )}
            <div className="flex justify-end mt-5">
              <button onClick={() => setCancelResult(null)} data-testid="refund-result-close" className="gp-btn-teal rounded-xl px-6 py-2.5 text-sm font-medium">Done</button>
            </div>
          </div>
        </div>
      )}

      {postponeModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setPostponeModal(null)}>
          <div className="bg-white rounded-2xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()} data-testid="postpone-modal">
            <div className="flex items-center gap-2 mb-1"><CalendarClock size={18} className="text-[#d97706]" /><h3 className="font-semibold text-[#16243b] text-lg">Postpone "{postponeModal.title}"</h3></div>
            <p className="text-sm text-[#8a97a8] mb-4">Tickets stay valid — visitors see a postponed notice and you can email everyone the new date.</p>
            <label className="block text-sm font-medium text-[#16243b] mb-1.5">New date (optional)</label>
            <input type="date" value={postponeDate} onChange={(e) => setPostponeDate(e.target.value)} data-testid="postpone-date"
              className="w-full rounded-xl border border-gray-200 px-4 py-2.5 outline-none focus:border-[#2ec9c9] text-sm mb-3" />
            <label className="block text-sm font-medium text-[#16243b] mb-1.5">Message to show / email (optional)</label>
            <textarea value={postponeMsg} onChange={(e) => setPostponeMsg(e.target.value)} data-testid="postpone-message" rows={2}
              placeholder="e.g. New date confirmed — same venue, same tickets."
              className="w-full rounded-xl border border-gray-200 px-4 py-2.5 outline-none focus:border-[#2ec9c9] text-sm" />
            <label className="flex items-center gap-2.5 mt-4 cursor-pointer">
              <input type="checkbox" checked={postponeNotify} onChange={(e) => setPostponeNotify(e.target.checked)} data-testid="postpone-notify" className="w-4 h-4 accent-[#2ec9c9]" />
              <span className="text-sm text-[#16243b]">Email all ticket holders the new date</span>
            </label>
            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => setPostponeModal(null)} className="gp-btn-outline rounded-xl px-5 py-2.5 text-sm font-medium">Back</button>
              <button onClick={confirmPostpone} disabled={postponeBusy} data-testid="postpone-confirm"
                className="rounded-xl px-6 py-2.5 text-sm font-medium bg-[#d97706] text-white hover:bg-[#b45309] disabled:opacity-60 flex items-center gap-2">
                {postponeBusy ? <Loader2 className="animate-spin" size={15} /> : <CalendarClock size={15} />} Postpone event
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

function Field({ label, value, onChange, type = "text", textarea, rows, required, placeholder, testid }) {
  return (
    <div>
      <label className="block text-sm font-medium text-[#16243b] mb-1.5">{label}</label>
      {textarea ? (
        <textarea value={value ?? ""} onChange={(e) => onChange(e.target.value)} rows={rows || 4} data-testid={testid} placeholder={placeholder}
          className="w-full rounded-xl border border-gray-200 px-4 py-2.5 outline-none focus:border-[#2ec9c9] focus:ring-2 focus:ring-[#2ec9c9]/20 resize-y min-h-[88px]" />
      ) : (
        <input type={type} value={value ?? ""} required={required} placeholder={placeholder} data-testid={testid}
          onChange={(e) => onChange(e.target.value)}
          className="w-full rounded-xl border border-gray-200 px-4 py-2.5 outline-none focus:border-[#2ec9c9] focus:ring-2 focus:ring-[#2ec9c9]/20" />
      )}
    </div>
  );
}
